import * as THREE from "three";
import { AudioManager } from "./AudioManager";
import {
  DEFAULT_LOOK_SENSITIVITY,
  applyCameraLook,
  clampLookSensitivity,
  yawToward,
} from "./CameraMath";
import {
  HANDS_CAPACITY,
  type RigKind,
  bobScale,
  canCarry,
  canRunWith,
  carriedBulk,
  carryDrain,
  carrySpeedFactor,
  missingEssentials,
  rigForCarry,
} from "./Carry";
import {
  BACKPACK_ID,
  BACKPACK_SLOTS,
  CARGO,
  CHAPTERS,
  CHAPTER_ORDER,
  DEFAULT_SAVE,
  FRIDGE_PICKS,
  LORE,
  STORAGE_KEY,
} from "./data";
import {
  moveCircleWithSliding,
  pointInsideAabb,
  positionIsBlocked,
  resolvePenetration,
  segmentCrossesAabb,
} from "./Collision";
import { InputController } from "./InputController";
import { MiniGames, type PackingItem } from "./MiniGames";
import type {
  ChapterId,
  Collider,
  DialogPage,
  GameSettings,
  Interactable,
  MovingHazard,
  SaveData,
  WorldContext,
} from "./types";
import { UI } from "./UI";
import { buildWorld } from "./WorldFactory";

const SETTINGS_KEY = "snitch-lan-settings-v1";
const BEST_SCORE_KEY = "snitch-lan-best-v1";
const PLAYER_HEIGHT = 1.72;
/** Assis dans la Corolla, on voit le monde trente-huit centimètres plus bas. */
const DRIVING_EYE_HEIGHT = 1.34;
const PLAYER_RADIUS = 0.42;
const VEHICLE_RADIUS = 0.92;

interface RendererLike {
  readonly domElement: HTMLCanvasElement;
  outputColorSpace: string;
  toneMapping: number;
  toneMappingExposure: number;
  shadowMap: {
    enabled: boolean;
    type: number;
  };
  setPixelRatio(value: number): void;
  setSize(width: number, height: number, updateStyle?: boolean): void;
  setAnimationLoop(callback: ((time: number) => void) | null): void;
  render(scene: THREE.Scene, camera: THREE.Camera): void;
}

class CompatibilityRenderer implements RendererLike {
  readonly domElement = document.createElement("canvas");
  outputColorSpace = THREE.SRGBColorSpace;
  toneMapping = THREE.NoToneMapping;
  toneMappingExposure = 1;
  shadowMap = { enabled: false, type: THREE.PCFShadowMap };
  private frame?: number;
  private ratio = 1;

  setPixelRatio(value: number): void {
    this.ratio = value;
  }

  setSize(width: number, height: number): void {
    this.domElement.width = Math.round(width * this.ratio);
    this.domElement.height = Math.round(height * this.ratio);
    this.domElement.style.width = `${width}px`;
    this.domElement.style.height = `${height}px`;
  }

  setAnimationLoop(callback: ((time: number) => void) | null): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    if (!callback) return;
    const loop = (time: number): void => {
      callback(time);
      this.frame = requestAnimationFrame(loop);
    };
    this.frame = requestAnimationFrame(loop);
  }

  render(_scene: THREE.Scene, camera: THREE.Camera): void {
    const context = this.domElement.getContext("2d");
    if (!context) return;
    const width = this.domElement.width;
    const height = this.domElement.height;
    context.fillStyle = "#070a11";
    context.fillRect(0, 0, width, height);
    context.strokeStyle = "rgba(87, 240, 210, .08)";
    context.lineWidth = Math.max(1, this.ratio);
    const offset = ((camera.rotation.y * 90 * this.ratio) % (54 * this.ratio)) + 54 * this.ratio;
    for (let x = -offset; x < width; x += 54 * this.ratio) {
      context.beginPath();
      context.moveTo(x, 0);
      context.lineTo(x, height);
      context.stroke();
    }
    for (let y = 0; y < height; y += 48 * this.ratio) {
      context.beginPath();
      context.moveTo(0, y);
      context.lineTo(width, y);
      context.stroke();
    }
  }
}

const DEFAULT_SETTINGS: GameSettings = {
  music: true,
  sound: true,
  reducedMotion: false,
  highContrast: false,
  pointerLock: true,
  lookSensitivity: DEFAULT_LOOK_SENSITIVITY,
};

/** Every archive terminal in the world, mapped to its entry in `LORE`. */
const LORE_BY_INTERACTABLE: Record<string, keyof typeof LORE> = {
  "lore-parking": "parking",
  "lore-lobby": "lobby",
  "lore-checkpoint": "checkpoint",
  "lore-hallA": "hallA",
  "lore-hallB": "hallB",
  "lore-seat": "seat",
  "lore-network": "network",
  "lore-diable": "diable",
  "lore-multiprise": "multiprise",
  "lore-registre": "registre",
};

/** Quel objet transportable se cache derrière quel prompt du monde. */
const CARGO_BY_INTERACTABLE: Record<string, string> = {
  "home-pc": "tour-gisèle",
  "home-monitor": "moniteur-crt",
  "home-peripherals": "bac-peripheriques",
  "home-chair": "chaise-gamer",
  "home-powerbar": "multiprise-maison",
  "home-cooler": "glaciere",
};

/** La porte fermée du prologue : un vrai obstacle, pas une décoration. */
const CLOSED_DOOR: Collider = { minX: 10.88, maxX: 11.12, minZ: -1.74, maxZ: -0.66 };

/** Où la voiture finit par se garer quand le créneau est réussi. */
const PARKED_SPOT = { x: 2.5, z: -14, yaw: Math.PI / 2 };

const START_YAW: Record<ChapterId, number> = {
  prologue: Math.PI,
  parking: 0,
  registration: 0,
  checkpoint: 0,
  hall: Math.PI,
  seat: Math.PI,
  network: 0,
  credits: Math.PI,
};

export class Game {
  private readonly ui: UI;
  private readonly input: InputController;
  private readonly audio: AudioManager;
  private readonly miniGames: MiniGames;
  private readonly renderer: RendererLike;
  private readonly camera: THREE.PerspectiveCamera;

  private scene = new THREE.Scene();
  private world?: WorldContext;
  private save: SaveData = this.freshSave();
  private settings: GameSettings;
  private animatedObjects: THREE.Object3D[] = [];
  private currentInteractable?: Interactable;
  /** Au volant ou à pied. Bascule en cours de chapitre, contrairement au monde. */
  private driving = false;
  /** Boîtes qui bougent : véhicules en maraude, voiture garée, porte fermée. */
  private dynamicColliders: Collider[] = [];
  private solidBodies: { hazard: MovingHazard; collider: Collider }[] = [];
  private running = false;
  private paused = false;
  private transitioning = false;
  private interactionBusy = false;
  private exhaustionBusy = false;
  /** True while the game itself is giving the lock back (menu, dialogue…). */
  private intentionalUnlock = false;
  private hintTimer?: number;
  private yaw = Math.PI;
  private pitch = 0;
  private stepPhase = 0;
  private stepSound = 0;
  private worldElapsed = 0;
  private autosaveElapsed = 0;
  private menuElapsed = 0;
  private lastFrame = performance.now();

  constructor(private readonly mount: HTMLElement) {
    this.settings = this.loadSettings();
    this.ui = new UI(mount);
    this.input = new InputController(mount);
    this.audio = new AudioManager(() => this.settings);
    this.miniGames = new MiniGames(this.ui, this.audio, () =>
      this.resumeControl(),
    );

    try {
      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        powerPreference: "high-performance",
      });
    } catch {
      this.renderer = new CompatibilityRenderer();
      document.documentElement.classList.add("no-webgl");
    }
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.32;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.domElement.setAttribute(
      "aria-label",
      "Vue à la première personne du LAN",
    );
    this.ui.canvasHost.append(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(70, 1, 0.08, 180);
    this.camera.rotation.order = "YXZ";
    this.input.bindLookTarget(this.renderer.domElement);
  }

  boot(): void {
    this.applySettingsClasses();
    this.resize();
    window.addEventListener("resize", () => this.resize());
    document.addEventListener("pointerlockchange", () =>
      this.onPointerLockChange(),
    );
    // Any click on the world reclaims the view — no dedicated button to find.
    this.renderer.domElement.addEventListener("click", () => {
      if (!this.input.pointerLocked) this.resumeControl();
    });

    this.ui.bindPause({
      onPause: () => this.pause(),
      onResume: () => this.resume(),
      onSettings: () => this.openSettings(),
      onMenu: () => this.returnToMenu(),
    });
    this.ui.bindInventory({
      onOpen: () => {
        this.ui.refreshInventory(this.save);
        this.releaseControl();
      },
      onClose: () => this.resumeControl(),
    });

    this.showMenuWorld();
    this.refreshMenuBindings();
    this.renderer.setAnimationLoop((time) => this.frame(time));
  }

  private refreshMenuBindings(): void {
    const saved = this.loadSave();
    this.ui.bindMenu(Boolean(saved), saved, {
      onNew: () => void this.newGame(),
      onContinue: () => {
        if (saved) void this.continueGame(saved);
      },
      onSettings: () => this.openSettings(),
    });
  }

  private async newGame(): Promise<void> {
    if (this.interactionBusy) return;
    this.releaseControl();
    const hero = await this.miniGames.chooseHero();
    this.save = this.freshSave();
    this.save.heroClass = hero;

    if (hero === "cpa") {
      this.save.stats.reputation = 12;
      this.save.flags.cpaClipboard = true;
    } else if (hero === "goblin") {
      this.save.stats.suspicion = 10;
      this.addItem("tournevis-gobelin");
    } else {
      this.save.flags.veteranSecondWind = true;
    }

    this.running = true;
    this.paused = false;
    this.ui.showMenu(false);
    this.audio.startMusic();
    await this.enterChapter("prologue");
    await this.say([
      {
        speaker: "MOI",
        portrait: "😵",
        text: "18 h 42. Je devais partir à 17 h. Gisèle installe encore une mise à jour qui promet de « ne prendre qu’un instant ».",
      },
      {
        speaker: "GISÈLE — TOUR BEIGE",
        portrait: "🖥️",
        text: "Mise à jour 99 %. Temps restant : entre douze secondes et la fin de l’univers.",
      },
      {
        speaker: "NARRATEUR NON SYNDIQUÉ",
        portrait: "📢",
        text: "Je n’ai qu’un objectif : atteindre la chaise 404, brancher Gisèle et jouer au moins une partie avant le déjeuner.",
      },
    ]);
    this.resumeControl();
  }

  private async continueGame(saved: SaveData): Promise<void> {
    this.save = saved;
    this.running = true;
    this.paused = false;
    this.ui.showMenu(false);
    this.audio.startMusic();
    await this.enterChapter(saved.chapter);
    this.ui.toast("Sauvegarde restaurée. Ta dignité, un peu moins.", "good");
    this.resumeControl();
  }

  private async enterChapter(chapter: ChapterId): Promise<void> {
    this.transitioning = true;
    this.releaseControl();
    this.input.clearAll();
    this.ui.setPrompt(null);

    // Gisèle finit sur la table : à partir du branchement, les mains sont libres.
    if (chapter === "network" || chapter === "credits") this.setIds("hands", []);

    this.scene = new THREE.Scene();
    this.world = buildWorld(chapter, this.scene, {
      interact: (id) => void this.interact(id),
      enabled: (id) => this.isInteractableEnabled(id),
      stowed: (cargoId) => this.isStowed(cargoId),
    });
    this.scene.fog = new THREE.FogExp2(this.world.fogColor, this.world.fogDensity ?? 0.018);
    this.save.chapter = chapter;
    this.save.checkpoint = CHAPTER_ORDER.indexOf(chapter);
    this.yaw = this.world.spawnLookAt
      ? yawToward(this.world.spawn, this.world.spawnLookAt)
      : START_YAW[chapter];
    this.pitch = 0;
    this.camera.position.copy(this.world.spawn);
    this.camera.position.y = PLAYER_HEIGHT;
    this.camera.rotation.set(this.pitch, this.yaw, 0);
    this.worldElapsed = 0;
    this.stepPhase = 0;
    this.cacheAnimatedObjects();

    this.ui.setChapter(chapter);
    this.ui.updateHUD(this.save);
    this.ui.refreshInventory(this.save);
    this.updateObjective();
    const inCredits = chapter === "credits";
    this.driving = Boolean(this.world.vehicleMode);
    this.camera.position.y = this.eyeHeight();
    this.restoreParkedCar();
    this.cacheSolidBodies();
    this.ui.setHUDVisible(!inCredits);
    this.refreshCarry();
    this.persist();

    await this.ui.showChapterCard(chapter);
    this.transitioning = false;

    if (inCredits) {
      this.showCredits();
    } else {
      this.resumeControl();
    }
  }

  private async interact(id: string): Promise<void> {
    if (
      this.interactionBusy ||
      this.transitioning ||
      this.paused ||
      this.ui.isModalOpen()
    ) {
      return;
    }
    this.interactionBusy = true;
    this.input.clearPressed();
    this.audio.play("click");

    try {
      if (id.startsWith("lore-")) {
        await this.readLore(id);
        return;
      }

      switch (id) {
        case "home-pc":
          await this.liftGisele();
          break;
        case "home-bag":
          await this.wearBackpack();
          break;
        case "home-monitor":
        case "home-peripherals":
        case "home-chair":
        case "home-powerbar":
        case "home-cooler":
          await this.pickUpProp(id);
          break;
        case "home-fridge":
          await this.raidFridge();
          break;
        case "home-door":
          await this.openFrontDoor();
          break;
        case "car-trunk":
          // Chez soi on charge, au stationnement on décharge.
          await (this.save.chapter === "parking" ? this.unloadTrunk() : this.useTrunk());
          break;
        case "car-drive":
          await this.leaveHome();
          break;
        case "car-board":
          await this.boardCar();
          break;
        case "parking-exit":
          await this.leaveParking();
          break;
        case "parking-dolly":
          await this.takeHandTruck();
          break;
        case "registration-queue":
          await this.handleQueueJumper();
          break;
        case "checkpoint-stash":
          await this.stashInsideGisele();
          break;
        case "hall-friends":
          await this.meetTheSquad();
          break;
        case "seat-camera":
          await this.requestFootage();
          break;
        case "parking-kiosk":
          await this.useParkingKiosk();
          break;
        case "parking-attendant":
          await this.talkParkingAttendant();
          break;
        case "parking-spot":
          await this.parkCar();
          break;
        case "phone-charger":
          await this.chargePhone();
          break;
        case "registration-board":
          await this.readRegistrationBoard();
          break;
        case "registration-helper":
          await this.talkRegistrationHelper();
          break;
        case "registration-clerk":
          await this.register();
          break;
        case "registration-exit":
          await this.leaveRegistration();
          break;
        case "packing-table":
          await this.packBag();
          break;
        case "security-guard":
          await this.talkSecurityGuard();
          break;
        case "surrender-bin":
          await this.inspectSurrenderBin();
          break;
        case "security-arch":
          await this.passSecurity();
          break;
        case "checkpoint-exit":
          await this.enterLanHall();
          break;
        case "coffee-machine":
          await this.drinkCoffee();
          break;
        case "tech-desk":
          await this.getExtensionCord();
          break;
        case "cable-crate":
          await this.getEthernetCable();
          break;
        case "snack-mage":
          await this.talkSnackMage();
          break;
        case "secret-usb":
          await this.takeSecretUsb();
          break;
        case "reserved-seat":
          await this.reachSeat();
          break;
        case "seat-witness":
          await this.questionWitness();
          break;
        case "claimed-chair":
          await this.examineChair();
          break;
        case "kevin":
          await this.confrontKevin();
          break;
        case "network-power":
          await this.connectPower();
          break;
        case "network-cable":
          await this.connectNetwork();
          break;
        case "network-boot":
          await this.bootGisele();
          break;
        case "router-boss":
          await this.fightRouter();
          break;
      }
    } finally {
      this.interactionBusy = false;
      this.normalizeStats();
      this.updateObjective();
      this.ui.updateHUD(this.save);
      this.ui.refreshInventory(this.save);
      this.persist();
      this.input.clearPressed();
      this.resumeControl();
    }
  }

  private async liftGisele(): Promise<void> {
    if (this.isStowed("tour-gisèle")) return;
    if (!canCarry(this.hands(), "tour-gisèle", CARGO)) {
      this.ui.toast(
        "Gisèle prend les deux bras. Il faut arriver les mains vides.",
        "bad",
      );
      this.audio.play("error");
      return;
    }

    if (!this.flag("homePc")) {
      await this.say([
        {
          speaker: "MOI",
          portrait: "🧔‍♂️",
          text: "Voici Gisèle : dix-huit kilos, un lecteur DVD qui ne s’ouvre plus et une lumière verte qui prouve juridiquement qu’elle fonctionne.",
        },
        {
          speaker: "GISÈLE",
          portrait: "🖥️",
          text: "Ventilateur principal : bruyant. Carte graphique : courageuse. Poignée de transport : purement décorative.",
        },
      ]);
      this.setFlag("homePc");
      this.addScore(150);
    }

    if (!this.takeCargo("home-pc")) return;
    await this.say([
      {
        speaker: "MOI",
        portrait: "😤",
        text: "Je plie les genoux, je mens à mon dos, et je soulève. Le monde se met à pencher légèrement vers l’avant pour le reste de la soirée.",
      },
    ]);
  }

  private async wearBackpack(): Promise<void> {
    if (this.wearingBag()) return;

    if (!this.flag("homeBag")) {
      const classLine =
        this.save.heroClass === "cpa"
          ? "J’ajoute aussi un cartable de pièces justificatives. Personne ne fouille volontairement là-dedans."
          : this.save.heroClass === "goblin"
            ? "Mon tournevis gobelin vibre déjà près des prises mal fixées."
            : "J’ai connu les LAN où il fallait transporter un écran cathodique. Ce sac ne me fait pas peur.";
      await this.say([
        {
          speaker: "MOI",
          portrait: "🎒",
          text: `Le sac contient un courriel imprimé sans code QR, trois câbles dont deux inconnus et une quantité préoccupante de miettes. ${classLine}`,
        },
      ]);
      this.setFlag("homeBag");
      this.addItem("lampe-frontale");
      this.addScore(100);
    }

    // Le sac se porte sur le dos : il ne coûte pas de bras, seulement du dos.
    this.setFlag("worn");
    this.addItem(BACKPACK_ID);
    this.hideProp("home-bag");
    this.audio.play("pickup");
    this.refreshCarry();
    this.ui.toast("Sac sur le dos. Reste à le remplir avant de partir.", "good");
  }

  private async pickUpProp(interactableId: string): Promise<void> {
    const before = this.hands().length;
    if (!this.takeCargo(interactableId)) return;
    if (before > 0) return;

    const lines: Record<string, DialogPage> = {
      "home-monitor": {
        speaker: "MOI",
        portrait: "📺",
        text: "L’écran cathodique. Dix-neuf pouces de verre et de mauvaise foi ergonomique. Il n’a pas de poignée non plus, évidemment.",
      },
      "home-peripherals": {
        speaker: "MOI",
        portrait: "⌨️",
        text: "Clavier, souris, tapis, et trois câbles que je n’ai jamais identifiés mais que je transporte depuis 2016 par superstition.",
      },
      "home-chair": {
        speaker: "MOI",
        portrait: "🪑",
        text: "La chaise. Un oncle me l’a donnée en disant « elle est correcte ». Une roulette part en biais depuis, mais mon dos la réclame.",
      },
      "home-powerbar": {
        speaker: "MOI",
        portrait: "🔌",
        text: "Six prises. Dans un LAN, ça ne s’appelle plus une multiprise, ça s’appelle un pouvoir de négociation.",
      },
      "home-cooler": {
        speaker: "MOI",
        portrait: "🧊",
        text: "La glacière. Elle fait déjà un bruit de marée et on n’a même pas quitté Châteauguay.",
      },
    };
    const page = lines[interactableId];
    if (page) await this.say([page]);
  }

  private async openFrontDoor(): Promise<void> {
    if (this.flag("doorOpen")) return;
    const full = this.carryBulk() >= HANDS_CAPACITY;
    await this.say([
      {
        speaker: "MOI",
        portrait: "🚪",
        text: full
          ? "Les deux bras pris, j’abaisse la poignée avec le coude, puis avec le menton, puis avec une partie de moi que je préfère ne pas nommer. La porte cède."
          : "J’ouvre la porte et je la cale avec la botte d’hiver que je n’ai jamais rangée. Elle sert enfin à quelque chose.",
      },
    ]);
    this.setFlag("doorOpen");
    this.syncDynamicColliders();
    this.audio.play("success");

    const pivot = this.world?.interactables
      .find((item) => item.id === "home-door")
      ?.object.getObjectByName("door-pivot");
    if (pivot) pivot.rotation.y = -1.42;
  }

  /** Le coffre : le seul endroit où la charge redevient nulle. */
  private async useTrunk(): Promise<void> {
    const carried = this.hands();
    if (carried.length) {
      this.setIds("trunk", [...this.trunkLoad(), ...carried]);
      this.setIds("hands", []);
      this.addScore(70 * carried.length);
      this.audio.play("success");
      this.refreshCarry();
    }

    const missing = missingEssentials(this.trunkLoad(), CARGO);
    const extras = this.trunkLoad().filter((id) => !CARGO[id]?.essential);
    await this.say([
      {
        speaker: "LE COFFRE",
        portrait: "🚗",
        text: carried.length
          ? `${carried.map((id) => CARGO[id]?.short ?? id).join(", ")} : déposé. Mes amortisseurs prennent note.`
          : "Rien dans les bras. Je reste ouvert, au cas où.",
      },
      {
        speaker: "INVENTAIRE DU COFFRE",
        portrait: "📋",
        text: missing.length
          ? `Il manque encore : ${missing.map((spec) => spec.short).join(", ")}.`
          : extras.length
            ? `Tout l’essentiel est dedans, plus ${extras.map((id) => CARGO[id]?.short ?? id).join(" et ")}. C’est un départ digne.`
            : "Tout l’essentiel est dedans. Le strict minimum, mais assumé.",
      },
    ]);
  }

  /**
   * Le frigo remplit le sac à dos. Ce qu'on y met revient trois chapitres plus
   * tard, devant un scanner, avec exactement le niveau de soupçon choisi ici.
   */
  private async raidFridge(): Promise<void> {
    if (this.flag("fridgeRaided")) return;
    if (!this.wearingBag()) {
      this.ui.toast("Remplir quoi? Le sac est encore par terre dans le salon.", "bad");
      this.audio.play("error");
      return;
    }

    await this.say([
      {
        speaker: "FRIGO",
        portrait: "🧊",
        text: "Contenu : trois condiments périmés, un reste de poutine ambitieux et la réserve de la St-Jean. Le LAN interdit tout ça, évidemment.",
      },
      {
        speaker: "MOI",
        portrait: "🤔",
        text: "Question stratégique : qu’est-ce que j’apporte, sachant que le contrôle des sacs existe et que mes amis comptent sur moi?",
      },
    ]);

    const result = await this.playMiniGame(() =>
      this.miniGames.fillBackpack(FRIDGE_PICKS as unknown as PackingItem[], BACKPACK_SLOTS),
    );

    this.setFlag("fridgeRaided");
    this.setIds("fridgePicks", result.items);
    result.items.forEach((item) => this.addItem(item));

    if (result.items.includes("punch-maison")) {
      this.setFlag("contraband", "punch-maison");
    } else if (result.items.includes("glace-seche")) {
      this.setFlag("contraband", "glace-seche");
    }

    // Le risque se paie au contrôle, pas dans la cuisine : ici, un acompte.
    this.addSuspicion(Math.round(result.suspicion * 0.25));
    this.addScore(Math.round(result.bonus * 0.45));

    if (!result.items.length) {
      this.addReputation(7);
      this.addSuspicion(-4);
      this.addScore(90);
      this.ui.toast("Sac vide, conscience nette. Personne ne t’en parlera plus jamais.", "good");
      return;
    }

    await this.say([
      {
        speaker: "LE SAC",
        portrait: "🎒",
        text:
          result.suspicion > 20
            ? "Je fais désormais un bruit que je ne peux pas expliquer à un bénévole."
            : "Chargé, fermé, raisonnable. Enfin, raisonnable selon tes critères.",
      },
    ]);
  }

  private async takeHandTruck(): Promise<void> {
    if (this.hasItem("diable")) return;
    await this.say([
      {
        speaker: "MOI",
        portrait: "🛒",
        text: "Un diable rouge appuyé sur le corral à paniers. Aucun cadenas, aucun propriétaire, une couche de rouille qui remonte à la première ère du Cat-5e.",
      },
      {
        speaker: "MON DOS",
        portrait: "🦴",
        text: "Prends-le. Prends-le tout de suite. Je te le demande officiellement, par écrit. Avec lui, tout ce qu’il y a dans le coffre monte d’un seul voyage.",
      },
    ]);
    this.addItem("diable");
    this.setFlag("hasDolly");
    this.addScore(360);
    this.addReputation(3);
    this.audio.play("pickup");
    this.hideProp("parking-dolly");
    this.refreshCarry();
    this.ui.toast("Le Diable est à toi. Gisèle pèse soudainement moins de dix-huit kilos.", "good");
  }

  private async handleQueueJumper(): Promise<void> {
    if (this.flag("queueSettled")) return;
    const answer = await this.say([
      {
        speaker: "LE_FAUFILEUX",
        portrait: "🦊",
        text: "Non non, j’étais là avant, je suis juste allé aux toilettes. Pendant quarante minutes. Avec mon manteau. Et mon char.",
      },
      {
        speaker: "MOI",
        portrait: "😑",
        text: "La file fait onze mètres et il vient de se matérialiser au troisième rang.",
        choices: [
          { label: "Le dénoncer poliment mais fermement", hint: "+réputation · +soupçon", action: () => undefined },
          { label: "Laisser faire, ça vaut pas la peine", hint: "−rien · silence intérieur", action: () => undefined },
          {
            label:
              this.save.heroClass === "cpa"
                ? "Lui demander sa pièce justificative"
                : "Prendre discrètement sa place derrière lui",
            hint: this.save.heroClass === "cpa" ? "Combo comptable" : "Chaotique",
            action: () => undefined,
          },
        ],
      },
    ]);

    this.setFlag("queueSettled");
    if (answer[1] === 0) {
      this.addReputation(10);
      this.addSuspicion(4);
      this.addScore(220);
      await this.say([
        {
          speaker: "LA FILE",
          portrait: "🧍‍♂️",
          text: "Un murmure d’approbation parcourt onze mètres d’humains fatigués. Quelqu’un applaudit une seule fois, puis le regrette.",
        },
      ]);
    } else if (answer[1] === 1) {
      this.addScore(80);
      this.ui.toast("Tu laisses passer. Ton karma le note, sans commentaire.", "neutral");
    } else if (this.save.heroClass === "cpa") {
      this.addReputation(14);
      this.addScore(320);
      await this.say([
        {
          speaker: "LE_FAUFILEUX",
          portrait: "🦊",
          text: "Une… pièce justificative? Pour une file? Pour une FILE?",
        },
        {
          speaker: "MOI",
          portrait: "🧾",
          text: "Sans pièce justificative, la transaction n’a jamais eu lieu. Il retourne à la fin de la file en marmonnant contre la comptabilité.",
        },
      ]);
    } else {
      this.addSuspicion(7);
      this.addScore(180);
      this.ui.toast("Tu gagnes deux places et une réputation légèrement radioactive.", "neutral");
    }
  }

  private async stashInsideGisele(): Promise<void> {
    if (this.flag("stashedInGisele")) return;
    const risky = ["rhum-pouch", "flasque", "mana-bleue", "punch-maison"].filter((item) =>
      this.hasItem(item),
    );
    if (!this.flag("bagPacked")) {
      this.ui.toast("Je devrais d’abord préparer mon sac à la table.", "bad");
      return;
    }
    if (!risky.length) {
      await this.say([
        {
          speaker: "GISÈLE",
          portrait: "🖥️",
          text: "Panneau latéral ouvert. Espace disponible : deux disques durs morts et beaucoup de poussière. Tu n’as rien à cacher là-dedans.",
        },
      ]);
      return;
    }

    const answer = await this.say([
      {
        speaker: "MOI",
        portrait: "🔧",
        text: "Le panneau latéral de Gisèle tient avec une vis et une promesse. Derrière le lecteur DVD condamné depuis 2013, il y a exactement l’espace d’un contenant suspect.",
        choices: [
          {
            label: "Cacher la contrebande dans la tour",
            hint: "−22 soupçon · Gisèle assume les risques",
            action: () => undefined,
          },
          { label: "Non. Gisèle a déjà assez souffert.", hint: "Dignité intacte", action: () => undefined },
        ],
      },
    ]);

    if (answer[0] !== 0) {
      this.addReputation(4);
      return;
    }

    this.setFlag("stashedInGisele", risky[0]);
    this.addSuspicion(-22);
    this.addScore(480);
    this.audio.play("pickup");
    await this.say([
      {
        speaker: "GISÈLE",
        portrait: "🖥️",
        text: "Nouveau périphérique détecté : liquide. Pilote introuvable. Je vais faire semblant de rien, mais on en reparlera au démarrage.",
      },
    ]);
  }

  private async meetTheSquad(): Promise<void> {
    if (this.flag("squadFound")) return;
    await this.say([
      {
        speaker: "JEAN-SÉBAS",
        portrait: "🍕",
        text: "AH! T’es arrivé! Ça fait deux heures qu’on garde la rangée. On a perdu trois places, une multiprise et un peu de nous-mêmes.",
      },
      {
        speaker: "TI-GUY",
        portrait: "📺",
        text: "Moi j’ai apporté mon CRT. Vingt-huit kilos. Personne m’a volé ma place. Personne veut être à côté non plus, mais c’est un détail.",
      },
      {
        speaker: "ROXY",
        portrait: "📋",
        text: "Mauvaise nouvelle : ta place, la G-404, elle est prise. Un gars avec des Doritos. Il dit qu’il « attend un ami ». Ça fait quatre heures.",
      },
      {
        speaker: "MOI",
        portrait: "😤",
        text: "Quatre heures. J’ai transporté dix-huit kilos de beige à travers un stationnement, un badge et un scanner pour ça.",
      },
    ]);
    this.setFlag("squadFound");
    this.addItem("gaffer-tape");
    this.addItem("multiprise-secrete");
    this.addReputation(12);
    this.addScore(420);
    this.save.stats.energy += 14;
    this.audio.play("pickup");
    this.ui.toast("La gang te refile du gaffer et une multiprise de contrebande.", "good");
  }

  private async requestFootage(): Promise<void> {
    if (this.flag("seatFootage")) return;
    if (!this.flag("seatWitness")) {
      this.ui.toast("Le bénévole des caméras veut d’abord un nom. Faudrait parler au témoin.", "bad");
      return;
    }
    await this.say([
      {
        speaker: "CAMÉRA 12",
        portrait: "📹",
        text: "Séquence 21 h 03 : un sac à dos bleu dépose un objet sur la chaise G-404. Séquence 23 h 47 : un homme déplace ledit sac avec le pied, en regardant ailleurs.",
      },
      {
        speaker: "BÉNÉVOLE DES CAMÉRAS",
        portrait: "🍿",
        text: "Honnêtement c’est le meilleur contenu de la soirée. Je te l’envoie. Mentionne-moi si tu gagnes.",
      },
    ]);
    this.setFlag("seatFootage");
    this.addItem("video-preuve");
    this.addReputation(6);
    this.addScore(300);
    this.audio.play("pickup");
  }

  private async leaveHome(): Promise<void> {
    const missing = missingEssentials(this.trunkLoad(), CARGO);
    if (missing.length) {
      this.ui.toast(
        `Pas encore. Il manque ${missing.map((spec) => spec.short).join(", ")} dans le coffre.`,
        "bad",
      );
      return;
    }
    if (!this.wearingBag()) {
      this.ui.toast("Le sac est encore dans le salon. Ça commence bien.", "bad");
      return;
    }
    if (this.hands().length) {
      this.ui.toast("On ne conduit pas avec un écran sur les genoux. Coffre.", "bad");
      return;
    }

    const extras = this.trunkLoad().filter((id) => !CARGO[id]?.essential);
    await this.say([
      {
        speaker: "MOI",
        portrait: "🚗",
        text: "Le coffre se referme sur une pile qui tient uniquement grâce à la géométrie et à l’espoir. Je m’assois. La suspension s’assoit avec moi.",
      },
      {
        speaker: "MOI",
        portrait: "🔑",
        text: extras.length
          ? `J’ai même pensé à ${extras.map((id) => CARGO[id]?.short ?? id).join(" et ")}. Ce soir, je suis quelqu’un d’organisé.`
          : "Le strict essentiel. Ce qui veut dire qu’il manque forcément quelque chose, et que je le découvrirai devant témoins.",
      },
    ]);
    this.addScore(240);
    await this.enterChapter("parking");
  }

  private async useParkingKiosk(): Promise<void> {
    if (this.flag("parkingTicket")) return;

    // Au volant, la borne est toujours à quarante centimètres de trop.
    if (this.driving) {
      const answer = await this.say([
        {
          speaker: "BORNE P-404",
          portrait: "🅿️",
          text: "Approchez le véhicule. Plus près. Encore. Non, trop près. Recommencez la manœuvre.",
        },
        {
          speaker: "MOI",
          portrait: "🚙",
          text: "La vitre est descendue. La borne est à quarante centimètres de trop, comme toutes les bornes depuis l’invention de l’automobile.",
          choices: [
            {
              label: "M’étirer par la fenêtre",
              hint: "Rapide · douloureux",
              action: () => undefined,
            },
            {
              label: "Descendre du véhicule",
              hint: "Digne · plus long",
              action: () => undefined,
            },
          ],
        },
      ]);

      if (answer[1] === 1) {
        this.exitVehicle();
        if (this.driving) return;
        await this.say([
          {
            speaker: "MOI",
            portrait: "🚶",
            text: "Je descends, je fais deux pas, j’appuie sur le bouton. Une file de trois voitures se forme derrière moi en un temps record.",
          },
        ]);
        this.addReputation(3);
      } else {
        await this.say([
          {
            speaker: "MOI",
            portrait: "😖",
            text: "Je m’étire. Mon épaule émet un son que je n’avais jamais entendu. Le ticket sort. Mon omoplate garde rancune.",
          },
        ]);
        this.save.stats.energy -= 6;
        this.addSuspicion(3);
        this.addScore(60);
      }
    }

    if (this.save.heroClass === "cpa") {
      await this.say([
        {
          speaker: "BORNE P-404",
          portrait: "🅿️",
          text: "Tarif événement : 48 $. Frais de commodité : 7 $. Frais d’affichage des frais : 2 $.",
        },
        {
          speaker: "MOI",
          portrait: "🧾",
          text: "Je demande le numéro de TPS, le numéro de TVQ et une ventilation des frais. La borne imprime un ticket gratuit par instinct de survie.",
        },
      ]);
      this.addReputation(8);
    } else {
      await this.say([
        {
          speaker: "BORNE P-404",
          portrait: "🅿️",
          text: "Transaction refusée : carte trop raisonnable. Appuie encore pour payer davantage.",
        },
        {
          speaker: "MOI",
          portrait: "💳",
          text: "Après quatre essais, la borne accepte mystérieusement le même paiement et imprime un ticket encore chaud.",
        },
      ]);
      this.addSuspicion(2);
    }
    this.setFlag("parkingTicket");
    this.addItem("ticket-parking");
    this.addScore(180);
    this.audio.play("pickup");
  }

  private async talkParkingAttendant(): Promise<void> {
    if (this.flag("parkingClue")) return;
    await this.say([
      {
        speaker: "MARTINE_DU_P",
        portrait: "🦺",
        text: "P-404? Continue tout droit, tourne à gauche quand la signalisation dit droite, puis cherche l’espace que personne ne trouve.",
      },
      {
        speaker: "MOI",
        portrait: "😐",
        text: "Je comprends moins bien, mais avec une grande précision.",
      },
    ]);
    this.setFlag("parkingClue");
    this.addReputation(4);
  }

  private async parkCar(): Promise<void> {
    if (!this.flag("parkingTicket")) {
      this.ui.toast("Il me faut le ticket de la borne avant d’occuper P-404.", "bad");
      return;
    }
    if (this.flag("parked")) return;
    if (!this.driving) {
      this.ui.toast("Se stationner à pied, c’est juste être debout. Reprends le volant.", "bad");
      return;
    }

    const result = await this.playMiniGame(() => this.miniGames.precisionParking());
    this.setFlag("parked");
    this.addSuspicion(result.misses * 2);
    this.addScore(Math.max(120, 520 - result.misses * 70));

    // La voiture prend sa place et le joueur en descend : la suite est à pied.
    const car = this.world?.playerCar;
    if (car) {
      car.position.set(PARKED_SPOT.x, 0, PARKED_SPOT.z);
      car.rotation.y = PARKED_SPOT.yaw;
      car.visible = true;
    }
    this.driving = false;
    this.syncDynamicColliders();
    if (!this.placeOnFoot(new THREE.Vector3(PARKED_SPOT.x, 0, PARKED_SPOT.z), Math.PI)) {
      this.camera.position.set(PARKED_SPOT.x, PLAYER_HEIGHT, PARKED_SPOT.z + 3.4);
    }
    this.camera.position.y = PLAYER_HEIGHT;
    this.refreshCarry();

    await this.say([
      {
        speaker: "MARTINE_DU_P — AU LOIN",
        portrait: "🦺",
        text:
          result.misses === 0
            ? "C’est le plus beau stationnement que j’ai vu depuis le Salon de l’auto 2008!"
            : "C’est dans les lignes si on choisit les bonnes lignes!",
      },
      {
        speaker: "MOI",
        portrait: "🚙",
        text: "Moteur coupé. Il reste le plus dur : sortir du coffre tout ce que j’y ai empilé avec tant de confiance il y a quarante minutes.",
      },
    ]);
  }

  /**
   * Le déchargement. Sans diable, les bras ne suffisent pas pour tout : c'est
   * ici que le détour par le corral à paniers se paie ou se regrette.
   */
  private async unloadTrunk(): Promise<void> {
    if (!this.flag("parked")) {
      this.ui.toast("Pas ici. Le char doit être dans P-404 avant de tout sortir.", "bad");
      return;
    }

    const load = this.trunkLoad();
    if (this.flag("unloaded")) {
      await this.say([
        {
          speaker: "LE COFFRE",
          portrait: "🚗",
          text: load.length
            ? `Il reste ${load.map((id) => CARGO[id]?.short ?? id).join(", ")}. On s’était entendus là-dessus.`
            : "Vide. Complètement vide. On a tout sorti, et j’en suis encore ému.",
        },
      ]);
      return;
    }

    if (this.onDolly()) {
      this.setIds("hands", ["tour-gisèle"]);
      load.forEach((id) => this.addItem(id));
      this.setIds("trunk", []);
      this.setFlag("unloaded");
      this.addScore(420);
      this.addReputation(4);
      this.refreshCarry();
      await this.say([
        {
          speaker: "MOI",
          portrait: "🛒",
          text: "Gisèle sur le diable. L’écran sur Gisèle. Le bac sur l’écran. La pile tient debout par une combinaison de sangles et de foi.",
        },
        {
          speaker: "MON DOS",
          portrait: "🦴",
          text: "Pour une fois, je n’ai rien à ajouter. Bonne soirée.",
        },
      ]);
      return;
    }

    const answer = await this.say([
      {
        speaker: "MOI",
        portrait: "🖥️",
        text: "Gisèle prend les deux bras. Il n’en reste aucun pour l’écran, ni pour le bac. Et le stationnement est à onze minutes de marche du hall.",
        choices: [
          {
            label: "Gisèle d’abord, je reviendrai",
            hint: "Sage · on ne revient jamais",
            action: () => undefined,
          },
          {
            label: "Tout empiler sur les bras",
            hint: "Tout garder · épuisant",
            action: () => undefined,
          },
        ],
      },
    ]);

    this.setFlag("unloaded");

    if (answer[0] === 1) {
      // Volontairement au-delà de la capacité : c'est le prix du refus de choisir.
      this.setIds("hands", load.filter((id) => CARGO[id]?.essential));
      load.forEach((id) => this.addItem(id));
      this.setIds("trunk", []);
      this.setFlag("overloaded");
      this.save.stats.energy -= 22;
      this.addSuspicion(6);
      this.addScore(300);
      this.refreshCarry();
      await this.say([
        {
          speaker: "MOI",
          portrait: "😵",
          text: "Je fais trois pas. L’écran glisse. Je le rattrape avec le menton. Un inconnu me dit « t’as besoin d’aide? » et je réponds « non » avec le peu de voix qu’il me reste.",
        },
      ]);
      return;
    }

    this.setIds("hands", ["tour-gisèle"]);
    this.addItem("tour-gisèle");
    if (this.wearingBag()) this.addItem(BACKPACK_ID);
    const abandoned = load.filter((id) => id !== "tour-gisèle");
    this.setIds("trunk", abandoned);
    if (abandoned.includes("moniteur-crt")) this.setFlag("noMonitor");
    if (abandoned.includes("bac-peripheriques")) this.setFlag("noPeripherals");
    this.addScore(150);
    this.refreshCarry();
    await this.say([
      {
        speaker: "MOI",
        portrait: "🚶",
        text: `Je referme le coffre sur ${abandoned.map((id) => CARGO[id]?.short ?? id).join(", ")}. « Je reviens dans dix minutes », que je dis. Personne n’est dupe, surtout pas moi.`,
      },
    ]);
  }

  private async leaveParking(): Promise<void> {
    if (this.driving) {
      this.ui.toast("Le hall n’a pas de service au volant. Descends.", "bad");
      return;
    }
    if (!this.flag("unloaded")) {
      this.ui.toast("Pas sans avoir vidé le coffre.", "bad");
      return;
    }
    await this.say([
      {
        speaker: "MOI",
        portrait: "🏢",
        text: "Le portique. Onze minutes de marche derrière moi, dix-huit kilos devant. La vraie soirée commence ici.",
      },
    ]);
    await this.enterChapter("registration");
  }

  private async chargePhone(): Promise<void> {
    if (this.flag("phoneCharged")) return;
    const result = await this.playMiniGame(() => this.miniGames.chargePhone());
    this.setFlag("phoneCharged");
    this.addItem("telephone-charge");
    this.addSuspicion(result.misses);
    this.addScore(Math.max(120, 420 - result.misses * 40));
    this.audio.play("coffee");
  }

  private async readRegistrationBoard(): Promise<void> {
    if (this.flag("registrationCode")) return;
    await this.say([
      {
        speaker: "ÉCRAN OFFICIEL",
        portrait: "📺",
        text: "MOT DU JOUR : CASTOR. NE PAS CONFONDRE AVEC LE MOT D’HIER : ORIGNAL. Les badges perdus sont maintenant une monnaie.",
      },
      {
        speaker: "MOI",
        portrait: "🦫",
        text: "Je mémorise CASTOR. Mon cerveau remplace immédiatement le nom d’un cousin pour faire de la place.",
      },
    ]);
    this.setFlag("registrationCode");
    this.addScore(100);
  }

  private async talkRegistrationHelper(): Promise<void> {
    if (this.flag("registrationHelp")) return;
    await this.say([
      {
        speaker: "BÉNÉVOLE_12",
        portrait: "🫡",
        text: "Pour le badge, il te faut le QR sur ton téléphone et le mot affiché sur l’écran. Non, le courriel imprimé n’a pas de QR. Oui, c’est drôle pour nous.",
      },
    ]);
    this.setFlag("registrationHelp");
    this.addReputation(3);
  }

  private async register(): Promise<void> {
    if (this.flag("badge")) return;
    if (!this.flag("phoneCharged") || !this.flag("registrationCode")) {
      const missing = [
        !this.flag("phoneCharged") ? "un téléphone vivant" : "",
        !this.flag("registrationCode") ? "le mot du jour" : "",
      ]
        .filter(Boolean)
        .join(" et ");
      await this.say([
        {
          speaker: "MÉLANIE.EXE",
          portrait: "🪪",
          text: `Dossier incomplet. Il me manque ${missing}. Je peux attendre : je suis alimentée par la file.`,
        },
      ]);
      return;
    }
    const result = await this.playMiniGame(() => this.miniGames.badgeCaptcha());
    this.setFlag("badge");
    this.addItem("badge");
    this.addSuspicion(result.misses * 2);
    this.addReputation(Math.max(2, 8 - result.misses));
    this.addScore(Math.max(180, 540 - result.misses * 60));
    await this.say([
      {
        speaker: "MÉLANIE.EXE",
        portrait: "🪪",
        text: "Badge compilé pour Packet_Pat. J’ai conservé la photo où tu clignais des deux yeux.",
      },
      {
        speaker: "MOI",
        portrait: "📛",
        text: "Je suis officiellement quelqu’un. Le badge le confirme avec une police Comic Sans.",
      },
    ]);
  }

  private async leaveRegistration(): Promise<void> {
    if (!this.flag("badge")) {
      this.ui.toast("La porte exige un badge et refuse mon charisme.", "bad");
      return;
    }
    await this.enterChapter("checkpoint");
  }

  private async packBag(): Promise<void> {
    if (this.flag("bagPacked")) return;
    // Ce qui a été mis dans le sac au frigo remonte ici, trois heures plus tard,
    // devant quelqu'un qui porte un dossard.
    const chosen = new Set(this.ids("fridgePicks"));
    const extras: PackingItem[] = FRIDGE_PICKS.filter(
      (pick) => chosen.has(pick.id) && pick.id !== "eau",
    ).map((pick) => ({ ...pick }));
    const result = await this.playMiniGame(() => this.miniGames.packBag(extras));
    result.items.forEach((item) => this.addItem(item));
    this.setFlag("bagPacked");
    this.save.flags.packedRisk = result.suspicion;
    this.addSuspicion(Math.round(result.suspicion * 0.45));
    this.addScore(result.bonus);
    await this.say([
      {
        speaker: "MOI",
        portrait: "🎒",
        text: "Le sac ferme. Il produit un petit « glou-glou » qui ne figurait pas dans le manuel.",
      },
      {
        speaker: "SAC TRANSPARENT",
        portrait: "😇",
        text: "Je n’ai rien vu. Je suis transparent, pas indiscret.",
      },
    ]);
  }

  private async talkSecurityGuard(): Promise<void> {
    if (this.flag("securityClue")) return;
    await this.say([
      {
        speaker: "AGENT PARE-FEU",
        portrait: "🛡️",
        text: "Traverse le scanner lentement, ne cours pas et garde une aura stable. Toute ressemblance avec une vraie procédure serait un accident.",
      },
      {
        speaker: "MOI",
        portrait: "😬",
        text: "Mon aura n’a jamais été stable, mais elle possède une garantie prolongée.",
      },
    ]);
    this.setFlag("securityClue");
  }

  private async inspectSurrenderBin(): Promise<void> {
    const risky = ["punch-maison", "rhum-pouch", "flasque", "mana-bleue"].filter((item) =>
      this.hasItem(item),
    );
    if (!risky.length || this.flag("surrenderedItem")) {
      await this.say([
        {
          speaker: "BAC DES REGRETS",
          portrait: "🗑️",
          text: "Le bac contient une bouteille de sauce forte, trois boissons tièdes et un watercooling qui demande un avocat.",
        },
      ]);
      return;
    }
    const labels: Record<string, string> = {
      "punch-maison": "punch maison",
      "rhum-pouch": "Rhum Pouch",
      flasque: "flasque « patch notes »",
      "mana-bleue": "Mana Bleue",
    };
    const item = risky[0];
    const answer = await this.say([
      {
        speaker: "BAC DES REGRETS",
        portrait: "🗑️",
        text: `Le ${labels[item]} semble nerveux. Le déposer réduirait le soupçon, mais aussi le panache.`,
        choices: [
          { label: `Déposer le ${labels[item]}`, hint: "−12 soupçon · +6 réputation", action: () => undefined },
          { label: "Conserver mes choix discutables", hint: "Plus de points au bilan", action: () => undefined },
        ],
      },
    ]);
    if (answer[0] === 0) {
      this.removeItem(item);
      this.setFlag("surrenderedItem", item);
      this.addSuspicion(-12);
      this.addReputation(6);
      this.ui.toast("Un choix responsable. Le bac est profondément déçu.", "good");
    }
  }

  private async passSecurity(): Promise<void> {
    if (this.flag("securityPassed")) return;
    if (!this.flag("bagPacked")) {
      this.ui.toast("Je devrais préparer mon sac à la table avant le scanner.", "bad");
      return;
    }
    const result = await this.playMiniGame(() => this.miniGames.securityScan());
    this.setFlag("securityPassed");
    this.addSuspicion(result.misses * (this.flag("stashedInGisele") ? 3 : 5));
    if (this.flag("stashedInGisele")) {
      await this.say([
        {
          speaker: "AGENT PARE-FEU",
          portrait: "🛡️",
          text: "Le sac est propre. Par contre ton ordinateur fait un bruit de vague quand tu marches.",
        },
        {
          speaker: "MOI",
          portrait: "😐",
          text: "C’est le refroidissement liquide. Il est… artisanal.",
        },
        {
          speaker: "AGENT PARE-FEU",
          portrait: "🛡️",
          text: "Je choisis de te croire, parce que la file derrière toi fait quarante mètres. Circule.",
        },
      ]);
      this.addScore(260);
    }
    if (result.misses >= 3) {
      this.save.stats.strikes += 1;
      this.addReputation(-4);
    } else {
      this.addReputation(8);
    }
    this.addScore(Math.max(180, 700 - result.misses * 90));
    await this.say([
      {
        speaker: "AGENT PARE-FEU",
        portrait: "🛡️",
        text:
          result.misses === 0
            ? "Profil parfaitement banal. C’est presque inquiétant. Circule."
            : "Le scanner a bipé, mais il bipe aussi devant les tables pliantes. Circule avec prudence.",
      },
      {
        speaker: "MOI",
        portrait: "😮‍💨",
        text: "Je suis entré. Mon sac aussi. Notre relation ne sera plus jamais la même.",
      },
    ]);
  }

  private async enterLanHall(): Promise<void> {
    if (!this.flag("securityPassed")) {
      this.ui.toast("L’agent Pare-Feu doit d’abord valider mon aura.", "bad");
      return;
    }
    await this.enterChapter("hall");
  }

  private async drinkCoffee(): Promise<void> {
    if (this.flag("coffeeUsed") && this.save.stats.energy > 70) {
      this.ui.toast("Un autre café me ferait voir le Wi-Fi à l’œil nu.", "neutral");
      return;
    }
    await this.say([
      {
        speaker: "MACHINE CAFÉ v0.3",
        portrait: "☕",
        text: "Sélection : BRUN. Ingrédients : chaleur, ambition et une trace de café.",
      },
      {
        speaker: "MOI",
        portrait: "⚡",
        text: "Ça goûte le reçu thermique, mais mes pensées recommencent à avoir des bordures.",
      },
    ]);
    this.setFlag("coffeeUsed");
    this.addItem("cafe");
    this.save.stats.energy += 38;
    this.addSuspicion(3);
    this.addScore(90);
    this.audio.play("coffee");
  }

  private async getExtensionCord(): Promise<void> {
    if (this.hasItem("rallonge")) return;
    const tradeItem = ["chips", "mana-bleue"].find((item) => this.hasItem(item));
    if (tradeItem) {
      const tradeName = tradeItem === "chips" ? "croustilles alibi" : "Mana Bleue";
      const result = await this.say([
        {
          speaker: "TECHNODAVE",
          portrait: "🧰",
          text: `J’ai une rallonge orange certifiée. Tu as du ${tradeName}. On pourrait créer une économie.`,
          choices: [
            { label: `Échanger le ${tradeName}`, hint: "Obtient la rallonge", action: () => undefined },
            { label: "Réciter le protocole de dépannage", hint: "Garde l’objet · +réputation", action: () => undefined },
          ],
        },
      ]);
      if (result[0] === 0) {
        this.removeItem(tradeItem);
      } else {
        this.addReputation(5);
      }
    } else if (this.save.heroClass === "cpa") {
      await this.say([
        {
          speaker: "MOI",
          portrait: "🧾",
          text: "Je lui présente une demande de rallonge en trois copies avec matrice d’autorisation.",
        },
        {
          speaker: "TECHNODAVE",
          portrait: "🧰",
          text: "Prends-la. Prends-en deux. Mais range ce cartable.",
        },
      ]);
      this.addReputation(6);
    } else {
      const answer = await this.say([
        {
          speaker: "TECHNODAVE",
          portrait: "🧰",
          text: "Question technique : as-tu essayé de l’éteindre puis de le rallumer?",
          choices: [
            { label: "Oui, mais avec conviction", action: () => undefined },
            { label: "Non, je préfère accuser le DNS", action: () => undefined },
            { label: "Je l’ai redémarré émotionnellement", action: () => undefined },
          ],
        },
      ]);
      if (answer[0] === 0) this.addReputation(4);
      else this.addSuspicion(2);
    }
    this.addItem("rallonge");
    this.setFlag("extensionFound");
    this.addScore(320);
    this.audio.play("pickup");
  }

  private async getEthernetCable(): Promise<void> {
    if (this.hasItem("cable-ethernet")) return;
    if (this.save.heroClass === "goblin") {
      await this.say([
        {
          speaker: "MOI",
          portrait: "👺",
          text: "Le nœud me parle dans la langue ancienne des attaches autobloquantes. Je tire exactement un fil et tout se démêle.",
        },
        {
          speaker: "CÂBLES",
          portrait: "🔌",
          text: "Le roi gobelin est revenu.",
        },
      ]);
      this.addScore(500);
    } else {
      const result = await this.playMiniGame(() => this.miniGames.untangleCable());
      this.addSuspicion(result.misses);
      this.addScore(Math.max(200, 560 - result.misses * 45));
    }
    this.addItem("cable-ethernet");
    this.setFlag("ethernetFound");
    this.audio.play("pickup");
  }

  private async talkSnackMage(): Promise<void> {
    if (this.flag("snackMage")) return;
    if (this.hasItem("mana-bleue")) {
      const result = await this.say([
        {
          speaker: "MAGESSE DU SNACK",
          portrait: "🥤",
          text: "Je reconnais la Mana Bleue. Donne-m’en une et je bénirai ton ping.",
          choices: [
            { label: "Partager une canette", hint: "+énergie · +réputation", action: () => undefined },
            { label: "Protéger le précieux", hint: "+score final", action: () => undefined },
          ],
        },
      ]);
      if (result[0] === 0) {
        this.removeItem("mana-bleue");
        this.save.stats.energy += 25;
        this.addReputation(9);
      } else {
        this.addScore(140);
      }
    } else {
      await this.say([
        {
          speaker: "MAGESSE DU SNACK",
          portrait: "🥤",
          text: "Je vends du fromage orange, de l’eau bleue et une barre protéinée qui connaît ton adresse IP.",
        },
      ]);
      this.save.stats.energy += 8;
    }
    this.setFlag("snackMage");
  }

  private async takeSecretUsb(): Promise<void> {
    if (this.flag("secretUsb")) return;
    await this.say([
      {
        speaker: "MOI",
        portrait: "💾",
        text: "Une clé USB anonyme au milieu d’un LAN. Toutes mes connaissances en sécurité crient non. Mon inventaire dit oui.",
      },
    ]);
    this.setFlag("secretUsb");
    this.addItem("usb-linux");
    this.addSuspicion(5);
    this.addScore(404);
    this.audio.play("pickup");
  }

  private async reachSeat(): Promise<void> {
    const missing: string[] = [];
    if (!this.hasItem("rallonge")) missing.push("une rallonge");
    if (!this.hasItem("cable-ethernet")) missing.push("un câble réseau");
    if (missing.length) {
      this.ui.toast(`Avant la place 404, il me faut ${missing.join(" et ")}.`, "bad");
      return;
    }
    await this.say([
      {
        speaker: "MOI",
        portrait: "🏁",
        text: "Je vois enfin G-404. Ma chaise. Ma table. Mon petit morceau de multiprise surchargée.",
      },
      {
        speaker: "NARRATEUR",
        portrait: "🧀",
        text: "La chaise est occupée par Kevin_2.4GHz, un homme qui mange des Doritos avec l’assurance d’un propriétaire foncier.",
      },
    ]);
    await this.enterChapter("seat");
  }

  private async questionWitness(): Promise<void> {
    if (this.flag("seatWitness")) return;
    await this.say([
      {
        speaker: "TÉMOIN_AFK",
        portrait: "😶",
        text: "J’étais AFK, mais ma webcam confirme que Kevin est arrivé après ton sac. Elle confirme aussi que je danse quand je crois être seul.",
      },
    ]);
    this.setFlag("seatWitness");
    this.addReputation(4);
    this.addScore(120);
  }

  private async examineChair(): Promise<void> {
    if (this.flag("seatProof")) return;
    await this.say([
      {
        speaker: "MOI",
        portrait: "🔎",
        text: "Sous la chaise : mon autocollant Packet_Pat, une capture de réservation froissée et assez de poudre orange pour dater l’arrivée de Kevin.",
      },
    ]);
    this.setFlag("seatProof");
    this.addItem("preuve-reservation");
    this.addScore(180);
  }

  private async confrontKevin(): Promise<void> {
    if (this.flag("seatWon")) return;
    const hasProof =
      this.flag("seatProof") && (this.flag("seatWitness") || this.flag("seatFootage"));
    const result = await this.playMiniGame(() => this.miniGames.seatDuel(hasProof));
    this.addSuspicion(result.misses * 2);
    if (!hasProof) {
      await this.say([
        {
          speaker: "KEVIN_2.4GHz",
          portrait: "🧀",
          text: "Ajournement accepté. Reviens avec une preuve et un témoin qui n’est pas AFK.",
        },
      ]);
      this.setFlag("duelAttempted");
      return;
    }
    this.setFlag("seatWon");
    this.addReputation(Math.max(8, 18 - result.misses * 2));
    this.addScore(Math.max(300, 820 - result.misses * 80));
    await this.say([
      {
        speaker: "KEVIN_2.4GHz",
        portrait: "🧀",
        text: "D’accord. Je me déplace à G-405. Mais je conserve mes miettes à titre d’améliorations locatives.",
      },
      {
        speaker: "MOI",
        portrait: "🪑",
        text: "Je récupère la chaise 404. Après tout ça, il ne reste qu’à brancher l’ordinateur. Donc évidemment, le réseau tombe.",
      },
    ]);
    await this.enterChapter("network");
  }

  private async connectPower(): Promise<void> {
    if (this.flag("powerConnected")) return;
    if (!this.hasItem("rallonge")) {
      this.ui.toast("La multiprise est trop loin. TechnoDave avait la rallonge.", "bad");
      return;
    }
    await this.say([
      {
        speaker: "MOI",
        portrait: "⚡",
        text: "Je branche la rallonge orange. Trois rangées de lumières baissent d’intensité en signe de respect.",
      },
    ]);
    this.setFlag("powerConnected");
    this.addScore(160);
  }

  private async connectNetwork(): Promise<void> {
    if (this.flag("networkConnected")) return;
    if (!this.hasItem("cable-ethernet")) {
      this.ui.toast("Le port réclame le Cat-5e ancestral du bac à câbles.", "bad");
      return;
    }
    await this.say([
      {
        speaker: "PORT RÉSEAU",
        portrait: "🔌",
        text: "Lien physique : oui. Confiance émotionnelle : en négociation.",
      },
    ]);
    this.setFlag("networkConnected");
    this.addScore(160);
  }

  private async bootGisele(): Promise<void> {
    if (this.flag("giseleBooted")) return;
    if (!this.flag("powerConnected") || !this.flag("networkConnected")) {
      this.ui.toast("Gisèle exige du courant ET un câble. Elle est vieille, pas magique.", "bad");
      return;
    }
    this.audio.play("boot");
    if (this.flag("noMonitor")) {
      await this.say([
        {
          speaker: "MOI",
          portrait: "😶",
          text: "Gisèle démarre. Je le sais parce qu’elle fait du bruit. Je ne le vois pas, parce que mon écran est resté dans le coffre, à onze minutes de marche.",
        },
        {
          speaker: "JEAN-SÉBAS",
          portrait: "🙂",
          text: "Tu peux regarder sur le mien. Penche-toi. Non, plus. Voilà, tu es parfait comme ça pour les six prochaines heures.",
        },
      ]);
      this.addSuspicion(4);
      this.addReputation(-3);
    }
    await this.say([
      {
        speaker: "GISÈLE",
        portrait: "🖥️",
        text: "BIOS 2009. Mémoire : probablement. Démarrage du système…",
      },
      {
        speaker: this.flag("noMonitor") ? "ÉCRAN EMPRUNTÉ" : "ÉCRAN",
        portrait: "📺",
        text: "ERREUR : AUCUN BAIL DHCP. Une présence nommée GÉRARD refuse l’adresse IP.",
      },
      {
        speaker: "MOI",
        portrait: "😤",
        text: "Je n’ai pas transporté dix-huit kilos de beige pour être vaincu par une case à cocher réseau.",
      },
    ]);
    if (this.flag("stashedInGisele")) {
      await this.say([
        {
          speaker: "GISÈLE",
          portrait: "🖥️",
          text: "Note interne : le ventilateur avant diffuse maintenant une odeur de punch. Trois personnes de la rangée G se sont retournées en même temps.",
        },
        {
          speaker: "ROXY — À DISTANCE",
          portrait: "📋",
          text: "C’est TOI qui sens comme un party de sous-sol de 2006?",
        },
        {
          speaker: "MOI",
          portrait: "😎",
          text: "C’est Gisèle. Elle a vécu.",
        },
      ]);
      this.addScore(320);
      this.addReputation(5);
    }
    this.setFlag("giseleBooted");
    this.addScore(250);
  }

  private async fightRouter(): Promise<void> {
    if (this.flag("routerDefeated")) return;
    if (!this.flag("giseleBooted")) {
      this.ui.toast("Je dois d’abord démarrer Gisèle pour invoquer Gérard-DHCP.", "bad");
      return;
    }
    await this.say([
      {
        speaker: "GÉRARD-DHCP",
        portrait: "👁️",
        text: "HUMAIN. TU AS DEMANDÉ UNE ADRESSE. JE T’OFFRE 169.254 ET LE SILENCE.",
      },
      {
        speaker: "MOI",
        portrait: "🗡️",
        text: "Je réclame le VLAN 404 au nom de Gisèle, de la chaise et de mon dos.",
      },
    ]);
    const result = await this.playMiniGame(() => this.miniGames.routerBoss());
    this.setFlag("routerDefeated");
    this.addSuspicion(result.misses * 2);
    this.addReputation(Math.max(10, 22 - result.misses));
    this.addScore(Math.max(600, 1400 - result.misses * 90));
    await this.say([
      {
        speaker: "GISÈLE",
        portrait: "🖥️",
        text: "Adresse obtenue : 192.168.0.404. Ping : 12 ms. Mise à jour obligatoire : 47 Go.",
      },
      {
        speaker: "MOI",
        portrait: "🥲",
        text: "Je suis enfin branché. Il est 3 h 47. La première partie commence dans environ quatre heures.",
      },
    ]);
    await this.enterChapter("credits");
  }

  private async readLore(id: string): Promise<void> {
    const key = LORE_BY_INTERACTABLE[id];
    if (!key || this.save.lore.includes(key)) return;
    const archive = LORE[key];
    await this.say([
      {
        speaker: archive.title,
        portrait: "📼",
        text: archive.body,
      },
    ]);
    this.save.lore.push(key);
    this.addScore(180);
    this.audio.play("pickup");
    if (this.save.lore.length === Object.keys(LORE).length) {
      this.ui.toast("Toutes les archives trouvées : la vérité est surtout du câble.", "good");
      this.addScore(700);
    }
  }

  private async say(pages: DialogPage[]): Promise<number[]> {
    // Straight dialogue advances with E, so the pointer lock can stay put. Only
    // branching pages need a visible cursor.
    if (pages.some((page) => page.choices?.length)) this.releaseControl();
    return this.ui.dialog(pages);
  }

  private async playMiniGame<T>(game: () => Promise<T>): Promise<T> {
    this.releaseControl();
    return game();
  }

  /* ---------------------------------------------------------------- *
   * Portage, coffre et véhicule
   * ---------------------------------------------------------------- */

  /**
   * Les listes d'objets vivent dans `flags` sous forme de texte séparé par des
   * virgules. `SaveData` n'accepte que des scalaires, et une vieille
   * sauvegarde qui ne connaît pas la clé revient simplement « mains vides »
   * plutôt qu'en `undefined.includes`.
   */
  private ids(name: string): string[] {
    const raw = this.save.flags[name];
    return typeof raw === "string" && raw.length ? raw.split(",") : [];
  }

  private setIds(name: string, ids: string[]): void {
    this.save.flags[name] = ids.join(",");
  }

  private hands(): string[] {
    return this.ids("hands");
  }

  private trunkLoad(): string[] {
    return this.ids("trunk");
  }

  private wearingBag(): boolean {
    return this.flag("worn");
  }

  private onDolly(): boolean {
    return this.hasItem("diable");
  }

  private carryBulk(): number {
    return carriedBulk(this.hands(), CARGO);
  }

  /**
   * Vrai quand l'objet n'est plus par terre : dans les bras ou dans le coffre.
   *
   * Volontairement aveugle à l'inventaire. Une sauvegarde écrite avant le
   * portage contient déjà « tour-gisèle » comme objet de départ : s'y fier
   * ferait disparaître Gisèle du salon d'une partie reprise, sans aucun moyen
   * de la charger.
   */
  private isStowed(cargoId: string): boolean {
    if (cargoId === BACKPACK_ID) return this.wearingBag();
    if (cargoId === "diable") return this.hasItem("diable");
    return this.hands().includes(cargoId) || this.trunkLoad().includes(cargoId);
  }

  private carryLabel(): string {
    const hands = this.hands();
    if (hands.length) {
      return hands.map((id) => CARGO[id]?.short ?? id).join(" + ");
    }
    return this.wearingBag() ? "Sac seulement" : "Vides";
  }

  /** Recale d'un coup le premier plan, la pastille de charge et le bouton tactile. */
  private refreshCarry(): void {
    const rig: RigKind = this.driving
      ? "wheel"
      : rigForCarry(this.hands(), CARGO, this.onDolly());
    const label = this.carryLabel();
    this.ui.setRig(rig, label);
    this.ui.setCarry(this.carryBulk(), HANDS_CAPACITY, label, this.driving);
    this.ui.setVehicleControl(Boolean(this.world?.playerCar), this.driving);
    this.syncDynamicColliders();
  }

  private eyeHeight(): number {
    return this.driving ? DRIVING_EYE_HEIGHT : PLAYER_HEIGHT;
  }

  private bodyRadius(): number {
    return this.driving ? VEHICLE_RADIUS : PLAYER_RADIUS;
  }

  /**
   * Retire du décor un objet qu'on vient de ramasser, colliders compris : un
   * sac emporté ne doit pas laisser une borne invisible sur le plancher.
   */
  private hideProp(interactableId: string): void {
    const world = this.world;
    const found = world?.interactables.find((item) => item.id === interactableId);
    if (!world || !found) return;
    found.object.visible = false;

    const owned = found.object.userData.colliders as Collider[] | undefined;
    for (const collider of owned ?? []) {
      const index = world.colliders.indexOf(collider);
      if (index >= 0) world.colliders.splice(index, 1);
    }
  }

  private cacheSolidBodies(): void {
    this.solidBodies = (this.world?.hazards ?? [])
      .filter((hazard) => hazard.solid)
      .map((hazard) => ({
        hazard,
        collider: { minX: 0, maxX: 0, minZ: 0, maxZ: 0 },
      }));
    this.syncDynamicColliders();
  }

  /**
   * Reconstruit la liste des obstacles mobiles. Appelée à chaque image pour les
   * véhicules, et à chaque changement d'état pour la voiture et la porte.
   */
  private syncDynamicColliders(): void {
    const colliders: Collider[] = [];

    for (const body of this.solidBodies) {
      const { hazard, collider } = body;
      const half = hazard.solid;
      if (!half) continue;
      const { x, z } = hazard.object.position;
      collider.minX = x - half.halfX;
      collider.maxX = x + half.halfX;
      collider.minZ = z - half.halfZ;
      collider.maxZ = z + half.halfZ;
      colliders.push(collider);
    }

    const car = this.world?.playerCar;
    const extent = this.world?.playerCarExtent;
    if (car && extent && car.visible && !this.driving) {
      const alongX = Math.abs(Math.cos(car.rotation.y)) > 0.5;
      const halfX = alongX ? extent.halfX : extent.halfZ;
      const halfZ = alongX ? extent.halfZ : extent.halfX;
      colliders.push({
        minX: car.position.x - halfX,
        maxX: car.position.x + halfX,
        minZ: car.position.z - halfZ,
        maxZ: car.position.z + halfZ,
      });
    }

    if (this.save.chapter === "prologue" && !this.flag("doorOpen")) {
      colliders.push(CLOSED_DOOR);
    }

    this.dynamicColliders = colliders;
  }

  private obstacles(): Collider[] {
    return [...(this.world?.colliders ?? []), ...this.dynamicColliders];
  }

  /**
   * Cherche autour d'un point une place libre où déposer le joueur à pied. Sans
   * ça, sortir de la voiture le collerait à l'intérieur de sa propre tôle, et
   * le rayon passant de 0.42 à 0.92 le figerait pour de bon.
   */
  private placeOnFoot(origin: THREE.Vector3, preferredYaw: number): boolean {
    const world = this.world;
    if (!world) return false;
    const obstacles = this.obstacles();

    for (let step = 0; step < 16; step += 1) {
      const swing = Math.ceil(step / 2) * (Math.PI / 5) * (step % 2 ? 1 : -1);
      const distance = 2.75 + Math.floor(step / 8) * 1.2;
      const angle = preferredYaw + swing;
      const x = origin.x + Math.sin(angle) * distance;
      const z = origin.z + Math.cos(angle) * distance;
      if (!positionIsBlocked(x, z, PLAYER_RADIUS, world.bounds, obstacles)) {
        this.camera.position.set(x, PLAYER_HEIGHT, z);
        return true;
      }
    }
    return false;
  }

  /** Replace la voiture là où le joueur l'avait laissée après un rechargement. */
  private restoreParkedCar(): void {
    const car = this.world?.playerCar;
    if (!car) return;
    if (!this.flag("parked")) {
      car.visible = false;
      return;
    }
    car.position.set(PARKED_SPOT.x, 0, PARKED_SPOT.z);
    car.rotation.y = PARKED_SPOT.yaw;
    car.visible = true;
    this.driving = false;
    this.camera.position.y = PLAYER_HEIGHT;
  }

  private toggleVehicle(): void {
    if (!this.world?.playerCar || this.interactionBusy || this.transitioning) return;
    if (this.driving) this.exitVehicle();
    else void this.boardCar();
  }

  private exitVehicle(): void {
    const world = this.world;
    const car = world?.playerCar;
    if (!world || !car || !this.driving) return;

    const parked = new THREE.Vector3(this.camera.position.x, 0, this.camera.position.z);
    car.position.copy(parked);
    car.rotation.y = this.yaw + Math.PI / 2;
    car.visible = true;
    this.driving = false;
    this.syncDynamicColliders();

    // La portière du conducteur est à gauche du capot.
    if (!this.placeOnFoot(parked, this.yaw - Math.PI / 2)) {
      car.visible = false;
      this.driving = true;
      this.syncDynamicColliders();
      this.ui.toast("Pas la place d’ouvrir la portière ici. Avance un peu.", "bad");
      this.audio.play("error");
      return;
    }

    this.audio.play("click");
    this.ui.toast("Tu descends. Le moteur cliquette en refroidissant.", "neutral");
    this.refreshCarry();
  }

  private async boardCar(): Promise<void> {
    const world = this.world;
    const car = world?.playerCar;
    if (!world || !car || this.driving) return;

    const distance = Math.hypot(
      car.position.x - this.camera.position.x,
      car.position.z - this.camera.position.z,
    );
    if (!car.visible || distance > 4.6) {
      this.ui.toast("Le char est plus loin que ça.", "bad");
      return;
    }
    if (this.flag("parked")) {
      this.ui.toast("On ne rebouge plus le char. La place P-404 est sacrée.", "bad");
      return;
    }
    if (this.hands().length) {
      this.ui.toast("Les mains pleines, on ne conduit pas. Ça se met dans le coffre.", "bad");
      return;
    }

    this.driving = true;
    car.visible = false;
    this.camera.position.set(car.position.x, DRIVING_EYE_HEIGHT, car.position.z);
    this.yaw = car.rotation.y - Math.PI / 2;
    this.pitch = 0;
    this.audio.play("boot");
    this.refreshCarry();
  }

  /** Ramasser un objet, si les bras suivent. */
  private takeCargo(interactableId: string): boolean {
    const cargoId = CARGO_BY_INTERACTABLE[interactableId];
    const spec = CARGO[cargoId];
    if (!spec || this.isStowed(cargoId)) return false;

    if (!canCarry(this.hands(), cargoId, CARGO)) {
      this.ui.toast(
        `Les bras sont pleins. ${spec.short} attendra le prochain voyage.`,
        "bad",
      );
      this.audio.play("error");
      return false;
    }

    this.setIds("hands", [...this.hands(), cargoId]);
    this.hideProp(interactableId);
    this.audio.play("pickup");
    this.refreshCarry();
    return true;
  }

  private isInteractableEnabled(id: string): boolean {
    if (id.startsWith("lore-")) {
      // Une archive se lit debout, pas par la fenêtre d'une voiture.
      if (this.driving) return false;
      return !this.save.lore.includes(LORE_BY_INTERACTABLE[id]);
    }

    // Tout ce qui se transporte disparaît du décor dès qu'il est embarqué.
    const cargoId = CARGO_BY_INTERACTABLE[id];
    if (cargoId) return !this.isStowed(cargoId);

    switch (id) {
      case "home-bag":
        return !this.wearingBag();
      case "home-door":
        return !this.flag("doorOpen");
      case "car-drive":
        return this.save.chapter === "prologue";
      case "car-board":
        return !this.driving && !this.flag("parked");
      case "car-trunk":
        // Au stationnement, le coffre ne s'ouvre qu'une fois la voiture garée :
        // le vider avant rendrait P-404 — qui exige le volant — inatteignable.
        return !this.driving && (this.save.chapter !== "parking" || this.flag("parked"));
      case "parking-exit":
        return !this.driving && this.flag("unloaded");
      case "parking-spot":
        return this.driving && !this.flag("parked");
      case "parking-dolly":
        return !this.driving && !this.flag("hasDolly");
      default:
        break;
    }

    const completionFlags: Record<string, string> = {
      "home-fridge": "fridgeRaided",
      "registration-queue": "queueSettled",
      "checkpoint-stash": "stashedInGisele",
      "hall-friends": "squadFound",
      "seat-camera": "seatFootage",
      "parking-kiosk": "parkingTicket",
      "parking-attendant": "parkingClue",
      "phone-charger": "phoneCharged",
      "registration-board": "registrationCode",
      "registration-helper": "registrationHelp",
      "registration-clerk": "badge",
      "packing-table": "bagPacked",
      "security-guard": "securityClue",
      "security-arch": "securityPassed",
      "coffee-machine": "coffeeUsed",
      "tech-desk": "extensionFound",
      "cable-crate": "ethernetFound",
      "snack-mage": "snackMage",
      "secret-usb": "secretUsb",
      "seat-witness": "seatWitness",
      "claimed-chair": "seatProof",
      kevin: "seatWon",
      "network-power": "powerConnected",
      "network-cable": "networkConnected",
      "network-boot": "giseleBooted",
      "router-boss": "routerDefeated",
    };
    const flag = completionFlags[id];
    return flag ? !this.flag(flag) : true;
  }

  private updateObjective(): void {
    switch (this.save.chapter) {
      case "prologue": {
        const loaded = this.trunkLoad();
        const essentials = Object.values(CARGO).filter((spec) => spec.essential);
        const done =
          essentials.filter((spec) => loaded.includes(spec.id)).length +
          Number(this.wearingBag());
        const total = essentials.length + 1;
        this.ui.setObjective(
          !this.wearingBag()
            ? "Enfiler le sac à dos, puis charger la voiture."
            : done < total
              ? "Charger Gisèle, l’écran et les périphériques dans le coffre."
              : "Embarquer et partir vers le LAN.",
          `${done}/${total}`,
        );
        break;
      }
      case "parking": {
        const count =
          Number(this.flag("parkingTicket")) +
          Number(this.flag("parked")) +
          Number(this.flag("unloaded"));
        this.ui.setObjective(
          this.flag("unloaded")
            ? "Rejoindre le portique du hall."
            : this.flag("parked")
              ? "Vider le coffre (un diable rendrait ça beaucoup plus simple)."
              : this.flag("parkingTicket")
                ? "Trouver P-404 et réussir le stationnement."
                : "Payer à la borne P-404, par la fenêtre ou à pied.",
          `${count}/3`,
        );
        break;
      }
      case "registration": {
        const count =
          Number(this.flag("phoneCharged")) +
          Number(this.flag("registrationCode")) +
          Number(this.flag("badge"));
        this.ui.setObjective(
          this.flag("badge")
            ? "Franchir la porte vers le contrôle."
            : "Charger le téléphone, trouver le mot et obtenir le badge.",
          `${count}/3`,
        );
        break;
      }
      case "checkpoint": {
        const count = Number(this.flag("bagPacked")) + Number(this.flag("securityPassed"));
        this.ui.setObjective(
          this.flag("securityPassed")
            ? "Entrer enfin dans la salle du LAN."
            : "Préparer le sac et traverser le scanner.",
          `${count}/2`,
        );
        break;
      }
      case "hall": {
        const count =
          Number(this.hasItem("rallonge")) +
          Number(this.hasItem("cable-ethernet")) +
          Number(this.flag("squadFound"));
        this.ui.setObjective(
          this.hasItem("rallonge") && this.hasItem("cable-ethernet")
            ? "Atteindre la place G-404."
            : "Retrouver la gang, une rallonge et un câble Ethernet.",
          `${count}/3`,
        );
        break;
      }
      case "seat": {
        const count =
          Number(this.flag("seatWitness")) +
          Number(this.flag("seatProof")) +
          Number(this.flag("seatFootage"));
        this.ui.setObjective(
          this.flag("seatWitness") && this.flag("seatProof")
            ? "Confronter Kevin_2.4GHz avec les preuves."
            : "Réunir une preuve, un témoin et si possible la vidéo.",
          `${count}/3`,
        );
        break;
      }
      case "network": {
        const count =
          Number(this.flag("powerConnected")) +
          Number(this.flag("networkConnected")) +
          Number(this.flag("giseleBooted")) +
          Number(this.flag("routerDefeated"));
        this.ui.setObjective(
          this.flag("giseleBooted")
            ? "Vaincre Gérard-DHCP."
            : "Brancher le courant, le réseau et démarrer Gisèle.",
          `${count}/4`,
        );
        break;
      }
      case "credits":
        this.ui.setObjective("Lire le générique sans pleurer sur le ping.");
        break;
    }
  }

  private frame(time: number): void {
    const delta = Math.min(0.05, Math.max(0, (time - this.lastFrame) / 1000));
    this.lastFrame = time;
    this.worldElapsed += delta;
    this.animateScene(delta, this.worldElapsed);
    this.world?.onUpdate?.(this.worldElapsed, delta);

    if (!this.running) {
      this.updateMenuCamera(delta);
    } else if (!this.paused && !this.transitioning && !this.ui.isModalOpen()) {
      this.save.elapsedSeconds += delta;
      this.autosaveElapsed += delta;
      this.updateFirstPerson(delta);
      this.updateHazards(time / 1000);
      this.updateFatigue(delta);
      this.updateInteractionFocus();
      this.consumeGameKeys();
      if (this.autosaveElapsed >= 5) {
        this.autosaveElapsed = 0;
        this.persist();
      }
    } else {
      this.input.clearLook();
      this.ui.setMoving(false);
      this.ui.setPrompt(null);
    }

    this.renderer.render(this.scene, this.camera);
  }

  private updateFirstPerson(delta: number): void {
    const look = this.input.consumeLook();
    const angles = applyCameraLook(
      { yaw: this.yaw, pitch: this.pitch },
      look,
      // Un volant tourne moins nerveusement qu'une nuque.
      this.settings.lookSensitivity * (this.driving ? 0.78 : 1),
    );
    this.yaw = angles.yaw;
    this.pitch = angles.pitch;

    const movement = this.input.movement;
    const amount = Math.hypot(movement.x, movement.y);
    const onDolly = this.onDolly();
    const bulk = this.driving ? 0 : this.carryBulk();
    const canRun =
      this.input.running &&
      this.save.stats.energy > 5 &&
      (this.driving || canRunWith(bulk, onDolly));
    const exhaustedFactor = this.save.stats.energy < 20 ? 0.76 : 1;
    const baseSpeed = this.driving ? 8.4 : 4.25;
    const dollyFactor = !this.driving && onDolly ? 1.12 : 1;
    const loadFactor = this.driving ? 1 : carrySpeedFactor(bulk, onDolly);
    const speed =
      baseSpeed * (canRun ? 1.55 : 1) * exhaustedFactor * dollyFactor * loadFactor;

    if (amount > 0.01 && this.world) {
      const localX = movement.x * speed * delta;
      const localZ = movement.y * speed * delta;
      const dx = localX * Math.cos(this.yaw) + localZ * Math.sin(this.yaw);
      const dz = -localX * Math.sin(this.yaw) + localZ * Math.cos(this.yaw);
      this.movePlayer(dx, dz);
      this.stepPhase += delta * speed * (this.driving ? 0.55 : 1.65);
      this.stepSound += delta * speed;
      if (this.stepSound > (canRun ? 2.25 : 2.85)) {
        this.stepSound = 0;
        if (!this.driving) this.audio.play("step");
      }
      if (!this.driving) {
        // Marcher chargé coûte, courir chargé coûte davantage.
        this.save.stats.energy -= delta * carryDrain(bulk, onDolly);
        if (canRun) this.save.stats.energy -= delta * (onDolly ? 0.62 : 1.15);
      }
    }

    const bob =
      amount > 0.01 && !this.settings.reducedMotion
        ? Math.sin(this.stepPhase * 2.1) *
          (canRun ? 0.055 : 0.032) *
          (this.driving ? 1 : bobScale(bulk))
        : 0;
    this.camera.position.y = this.eyeHeight() + bob;
    this.camera.rotation.set(this.pitch, this.yaw, 0);
    this.ui.setMoving(amount > 0.01, canRun);
  }

  private movePlayer(dx: number, dz: number): void {
    if (!this.world) return;
    const resolved = moveCircleWithSliding(
      { x: this.camera.position.x, z: this.camera.position.z },
      { x: dx, z: dz },
      this.bodyRadius(),
      this.world.bounds,
      this.obstacles(),
    );
    this.camera.position.x = resolved.x;
    this.camera.position.z = resolved.z;
  }

  private updateInteractionFocus(): void {
    if (!this.world) return;
    const forward = new THREE.Vector3();
    this.camera.getWorldDirection(forward);
    forward.y = 0;
    forward.normalize();
    const player = this.camera.position;
    let best: { interactable: Interactable; score: number } | undefined;

    for (const interactable of this.world.interactables) {
      if (!interactable.enabled() || !interactable.object.visible) continue;
      const target = interactable.focusOffset
        ? interactable.object.localToWorld(interactable.focusOffset.clone())
        : interactable.object.getWorldPosition(new THREE.Vector3());
      const dx = target.x - player.x;
      const dz = target.z - player.z;
      const distance = Math.hypot(dx, dz);
      if (distance > interactable.radius) continue;
      const direction =
        distance < 0.01
          ? forward
          : new THREE.Vector3(dx / distance, 0, dz / distance);
      const gaze = forward.dot(direction);
      const minimumGaze = this.finePointer() ? 0.42 : 0.08;
      if (gaze < minimumGaze) continue;

      const blocked = this.world.colliders.some((collider) => {
        // An interactive anchor is often placed just proud of its own surface.
        // A small grace envelope keeps the prop from occluding its own prompt.
        if (pointInsideAabb(target.x, target.z, collider, 0.42)) return false;
        return segmentCrossesAabb(
          { x: player.x, z: player.z },
          { x: target.x, z: target.z },
          collider,
        );
      });
      if (blocked) continue;

      const score = gaze * 3.2 - distance / interactable.radius;
      if (!best || score > best.score) best = { interactable, score };
    }

    this.currentInteractable = best?.interactable;
    this.ui.setPrompt(best?.interactable.label ?? null);
  }

  private consumeGameKeys(): void {
    if (this.input.consume("KeyE") && this.currentInteractable) {
      this.currentInteractable.onInteract();
    }
    if (this.input.consume("KeyF")) this.toggleVehicle();
    if (this.input.consume("KeyI") || this.input.consume("Tab")) {
      this.ui.refreshInventory(this.save);
      this.ui.showInventory(true);
      this.releaseControl();
    }
    if (this.input.consume("Escape")) this.pause();
  }

  private updateHazards(now: number): void {
    if (!this.world) return;
    for (const hazard of this.world.hazards) {
      const value =
        hazard.origin +
        Math.sin(now * hazard.speed * Math.PI * 2 + hazard.phase) * hazard.amplitude;
      hazard.object.position[hazard.axis] = value;
      hazard.object.rotation.y =
        Math.cos(now * hazard.speed * Math.PI * 2 + hazard.phase) > 0
          ? 0
          : Math.PI;
      hazard.object.updateMatrixWorld();
      const distance = Math.hypot(
        hazard.object.position.x - this.camera.position.x,
        hazard.object.position.z - this.camera.position.z,
      );
      if (distance < hazard.radius + 0.55 && now - hazard.lastHit > 2.2) {
        hazard.lastHit = now;
        this.save.stats.energy -= hazard.damage;
        this.addSuspicion(Math.ceil(hazard.damage * 0.55));
        this.ui.toast(hazard.message, "bad");
        this.audio.play("alert");
        document.documentElement.classList.add("impact");
        window.setTimeout(() => document.documentElement.classList.remove("impact"), 260);
      }
    }

    // Les boîtes suivent leur objet, puis on repousse le joueur qui s'est fait
    // rattraper. Sans ce dégagement, une navette qui balaie un joueur immobile
    // le clouerait sur place : toutes les directions seraient bloquées.
    this.syncDynamicColliders();
    const pushed = resolvePenetration(
      { x: this.camera.position.x, z: this.camera.position.z },
      this.bodyRadius(),
      this.world.bounds,
      this.dynamicColliders,
    );
    this.camera.position.x = pushed.x;
    this.camera.position.z = pushed.z;
  }

  private updateFatigue(delta: number): void {
    if (["hall", "seat", "network"].includes(this.save.chapter)) {
      // Eighteen kilos on a hand truck is a very different eighteen kilos.
      const overload = this.flag("overloaded") ? 2.1 : 1;
      this.save.stats.energy -= delta * (this.onDolly() ? 0.018 : 0.045) * overload;
    }
    if (
      Math.hypot(this.input.movement.x, this.input.movement.y) < 0.05 &&
      this.save.stats.energy < 100
    ) {
      this.save.stats.energy += delta * 0.18;
    }
    this.save.stats.suspicion = Math.max(0, this.save.stats.suspicion - delta * 0.012);
    this.ui.updateHUD(this.save);

    if (this.save.stats.energy <= 0 && !this.exhaustionBusy) {
      void this.handleExhaustion();
    } else if (this.save.stats.suspicion >= 100 && !this.exhaustionBusy) {
      void this.handleInspection();
    }
  }

  private async handleExhaustion(): Promise<void> {
    this.exhaustionBusy = true;
    this.interactionBusy = true;
    if (this.flag("veteranSecondWind")) {
      this.setFlag("veteranSecondWind", false);
      this.save.stats.energy = 42;
      await this.say([
        {
          speaker: "MÉMOIRE DU 56K",
          portrait: "📞",
          text: "Le cri d’un modem me traverse. Je retrouve une réserve d’énergie datant de 2001.",
        },
      ]);
    } else {
      await this.say([
        {
          speaker: "MOI",
          portrait: "💤",
          text: "Je ferme les yeux une seconde. Vingt-trois minutes et un rêve de poutine en RAID plus tard, je me réveille au point d’entrée.",
        },
      ]);
      this.save.stats.energy = 36;
      this.save.stats.strikes += 1;
      this.addReputation(-4);
      if (this.world) {
        this.camera.position.copy(this.world.spawn);
        this.camera.position.y = this.eyeHeight();
      }
    }
    this.interactionBusy = false;
    this.exhaustionBusy = false;
    this.normalizeStats();
    this.persist();
    this.resumeControl();
  }

  private async handleInspection(): Promise<void> {
    this.exhaustionBusy = true;
    this.interactionBusy = true;
    const confiscated = ["punch-maison", "rhum-pouch", "flasque", "mana-bleue"].find((item) =>
      this.hasItem(item),
    );
    await this.say([
      {
        speaker: "AGENT PARE-FEU — APPARITION",
        portrait: "🛡️",
        text: confiscated
          ? "Ton compteur de soupçon déborde. Je confisque l’objet qui fait le plus de bruit liquide et je te remets en circulation."
          : "Ton compteur de soupçon déborde sans aucune preuve matérielle. C’est presque impressionnant. Respire et recommence.",
      },
    ]);
    if (confiscated) this.removeItem(confiscated);
    this.save.stats.suspicion = 58;
    this.save.stats.strikes += 1;
    this.addScore(-300);
    if (this.world) {
      this.camera.position.copy(this.world.spawn);
      this.camera.position.y = this.eyeHeight();
    }
    this.interactionBusy = false;
    this.exhaustionBusy = false;
    this.persist();
    this.resumeControl();
  }

  private animateScene(delta: number, elapsed: number): void {
    for (const object of this.animatedObjects) {
      const spin = Number(object.userData.spin ?? 0);
      if (spin) object.rotation.y += spin * delta;

      if (object.userData.bob !== undefined) {
        const phase = Number(object.userData.bob);
        if (object.userData.baseY === undefined) object.userData.baseY = object.position.y;
        const amount = this.settings.reducedMotion ? 0.012 : 0.055;
        object.position.y =
          Number(object.userData.baseY) + Math.sin(elapsed * 1.8 + phase) * amount;
      }

      if (object.userData.pulse) {
        if (!object.userData.baseScale) {
          object.userData.baseScale = [
            object.scale.x,
            object.scale.y,
            object.scale.z,
          ];
        }
        const base = object.userData.baseScale as [number, number, number];
        const pulse = this.settings.reducedMotion
          ? 1
          : 1 + Math.sin(elapsed * 3.4) * 0.055;
        object.scale.set(base[0] * pulse, base[1] * pulse, base[2] * pulse);
      }
    }
  }

  private cacheAnimatedObjects(): void {
    this.animatedObjects = [];
    this.scene.traverse((object) => {
      if (
        object.userData.spin !== undefined ||
        object.userData.bob !== undefined ||
        object.userData.pulse
      ) {
        this.animatedObjects.push(object);
      }
    });
  }

  private showMenuWorld(): void {
    this.running = false;
    this.scene = new THREE.Scene();
    this.world = buildWorld("prologue", this.scene, {
      interact: () => undefined,
      enabled: () => false,
    });
    this.cacheAnimatedObjects();
    this.camera.position.set(-3.2, 2.15, 7.4);
    this.camera.lookAt(3.8, 0.85, 4.2);
    this.ui.showMenu(true);
    this.ui.setHUDVisible(false);
    this.ui.setRig("none");
  }

  private updateMenuCamera(delta: number): void {
    this.menuElapsed += delta;
    const angle = this.menuElapsed * 0.085;
    this.camera.position.set(
      -3 + Math.sin(angle) * 2.6,
      2.15 + Math.sin(angle * 1.7) * 0.16,
      6.6 + Math.cos(angle) * 1.6,
    );
    this.camera.lookAt(3.8, 0.85, 4.2);
  }

  private pause(): void {
    if (!this.running || this.paused || this.transitioning || this.ui.isModalOpen()) return;
    this.paused = true;
    this.releaseControl();
    this.ui.showPause(true);
    this.persist();
  }

  private resume(): void {
    if (!this.paused) return;
    this.paused = false;
    this.ui.showPause(false);
    this.input.clearAll();
    this.resumeControl();
  }

  private openSettings(): void {
    this.releaseControl();
    this.ui.showSettings(
      { ...this.settings },
      (settings) => {
        const wasLocking = this.settings.pointerLock;
        this.settings = settings;
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
        this.applySettingsClasses();
        if (wasLocking && !settings.pointerLock) this.releaseControl();
        if (settings.music && this.running) this.audio.startMusic();
        else this.audio.stopMusic();
      },
      () => {
        if (this.running && !this.paused) this.resumeControl();
      },
    );
  }

  private returnToMenu(): void {
    this.running = false;
    this.paused = false;
    this.transitioning = false;
    this.interactionBusy = false;
    this.releaseControl();
    this.input.clearAll();
    this.audio.stopMusic();
    this.ui.showPause(false);
    this.ui.showInventory(false);
    this.ui.closePanel();
    this.showMenuWorld();
    this.refreshMenuBindings();
  }

  /**
   * Hands control back to the player. Menus, dialogue and mini-games all close
   * from a click or a key press, so the browser still trusts us with a pointer
   * lock request — no "click here to continue" wall in between.
   */
  private resumeControl(): void {
    window.clearTimeout(this.hintTimer);
    if (!this.canUseFirstPersonControl()) {
      this.ui.setControlHint(false);
      return;
    }
    if (!this.finePointer() || !this.settings.pointerLock) {
      // Touch and drag-to-look need no lock at all.
      this.ui.setControlHint(false);
      return;
    }
    if (this.input.pointerLocked) {
      this.ui.setControlHint(false);
      return;
    }

    this.intentionalUnlock = false;
    this.input.requestPointerLock();
    this.hintTimer = window.setTimeout(() => {
      const stillNeeded =
        this.canUseFirstPersonControl() && this.settings.pointerLock && !this.input.pointerLocked;
      this.ui.setControlHint(stillNeeded);
    }, 220);
  }

  /** Gives the lock back on purpose, so losing it does not trigger a pause. */
  private releaseControl(): void {
    window.clearTimeout(this.hintTimer);
    this.ui.setControlHint(false);
    if (this.input.pointerLocked) this.intentionalUnlock = true;
    this.input.releasePointerLock();
  }

  private onPointerLockChange(): void {
    if (this.input.pointerLocked) {
      this.intentionalUnlock = false;
      this.ui.setControlHint(false);
      this.input.clearLook();
      if (!this.canUseFirstPersonControl()) this.releaseControl();
      return;
    }

    this.input.clearAll();
    if (this.intentionalUnlock) {
      this.intentionalUnlock = false;
      return;
    }
    // Escape or a lost window: pause on purpose instead of nagging for a click.
    if (this.canUseFirstPersonControl()) this.pause();
    else this.ui.setControlHint(false);
  }

  private canUseFirstPersonControl(): boolean {
    return (
      this.running &&
      !this.paused &&
      !this.transitioning &&
      !this.interactionBusy &&
      !this.ui.isModalOpen() &&
      this.save.chapter !== "credits"
    );
  }

  private showCredits(): void {
    this.releaseControl();
    this.ui.setHUDVisible(false);
    this.ui.setRig("none");
    this.audio.stopMusic();

    const finalScore = this.calculateFinalScore();
    const oldBest = Number(localStorage.getItem(BEST_SCORE_KEY) ?? 0);
    const isBest = finalScore > oldBest;
    if (isBest) localStorage.setItem(BEST_SCORE_KEY, String(finalScore));
    const rank =
      finalScore >= 7000 ? "S" : finalScore >= 5600 ? "A" : finalScore >= 4200 ? "B" : "C";
    const ending = this.endingText();
    const minutes = Math.floor(this.save.elapsedSeconds / 60);
    const seconds = Math.floor(this.save.elapsedSeconds % 60);
    const time = `${minutes}:${String(seconds).padStart(2, "0")}`;

    const body = this.ui.openPanel(
      "GG, probablement",
      "Tu as atteint le générique avec un ordinateur encore techniquement transportable.",
    );
    body.innerHTML = `
      <div class="credits-layout">
        <section class="ending-card">
          <small>FIN OBTENUE</small>
          <h3>${ending.title}</h3>
          <p>${ending.text}</p>
          <div class="final-rank"><span>RANG</span><strong>${rank}</strong></div>
        </section>
        <section class="score-card">
          <div><small>SCORE FINAL</small><strong>${finalScore.toLocaleString("fr-CA")}</strong>${isBest ? "<b>NOUVEAU RECORD LOCAL</b>" : ""}</div>
          <dl>
            <div><dt>Temps</dt><dd>${time}</dd></div>
            <div><dt>Soupçon</dt><dd>${Math.round(this.save.stats.suspicion)} %</dd></div>
            <div><dt>Réputation</dt><dd>${Math.round(this.save.stats.reputation)} %</dd></div>
            <div><dt>Archives</dt><dd>${this.save.lore.length} / ${Object.keys(LORE).length}</dd></div>
            <div><dt>Interventions</dt><dd>${this.save.stats.strikes}</dd></div>
            <div><dt>Énergie</dt><dd>${Math.round(this.save.stats.energy)} %</dd></div>
          </dl>
        </section>
        <section class="credits-roll">
          <small>SNITCH LAN — LA CHAISE 404</small>
          <h3>Une aventure en vue subjective</h3>
          <p><b>Transport lourd</b><br>Gisèle, tour beige 18 kg</p>
          <p><b>Administration</b><br>Mélanie.exe et la borne P-404</p>
          <p><b>Jurisprudence des sièges</b><br>Kevin_2.4GHz</p>
          <p><b>Antagoniste réseau</b><br>Gérard-DHCP</p>
          <p><b>Conseils nutritionnels</b><br>Une machine à café v0.3</p>
          <p><b>Aucun remerciement à</b><br>La mise à jour de 47 Go</p>
          <blockquote>« La vraie victoire, c’était le ping rencontré en chemin. »</blockquote>
        </section>
      </div>
      <div class="credits-actions">
        <button class="button primary" data-replay>Rejouer avec une autre mauvaise idée</button>
        <button class="button" data-credits-menu>Retour au menu</button>
      </div>
      <p class="panel-footnote">Fiction parodique sans affiliation à un événement réel. N’apporte pas de Rhum Pouch pour vrai.</p>
    `;
    body.querySelector<HTMLButtonElement>("[data-replay]")!.onclick = () => {
      this.ui.closePanel();
      localStorage.removeItem(STORAGE_KEY);
      void this.newGame();
    };
    body.querySelector<HTMLButtonElement>("[data-credits-menu]")!.onclick = () =>
      this.returnToMenu();
    this.persist();
  }

  private calculateFinalScore(): number {
    const timeBonus = Math.max(0, 1800 - this.save.elapsedSeconds) * 0.75;
    const stealthBonus = Math.max(0, 100 - this.save.stats.suspicion) * 9;
    const reputationBonus = this.save.stats.reputation * 8;
    const archiveBonus = this.save.lore.length * 240;
    const energyBonus = this.save.stats.energy * 4;
    return Math.max(
      0,
      Math.round(
        this.save.stats.score +
          timeBonus +
          stealthBonus +
          reputationBonus +
          archiveBonus +
          energyBonus -
          this.save.stats.strikes * 220,
      ),
    );
  }

  private endingText(): { title: string; text: string } {
    // Ce qu'on a laissé dans le coffre a le dernier mot avant le générique.
    if (this.flag("noMonitor")) {
      return {
        title: "L’homme qui jouait sur l’écran d’un autre",
        text: "Gisèle est branchée. L’écran, lui, est resté dans le coffre, à onze minutes de marche. Tu as passé la nuit à moitié penché sur le moniteur du voisin, qui a été très gentil et ne t’a jamais rien dit.",
      };
    }
    if (this.flag("overloaded")) {
      return {
        title: "Le porteur intégral",
        text: "Tu as tout monté d’un seul voyage : la tour, l’écran, le bac, le sac. Ton dos a déposé une plainte formelle que tu liras en 2031. Tout le monde a vu. Personne n’oubliera.",
      };
    }
    if (this.save.lore.length === Object.keys(LORE).length) {
      return {
        title: "L’Archiviste du câble perdu",
        text: "Tu as branché Gisèle et reconstitué toute l’histoire honteuse du LAN. Gérard-DHCP te craint désormais par ton nom d’hôte.",
      };
    }
    if (this.save.stats.reputation >= 65) {
      return {
        title: "Administrateur honoraire du VLAN 404",
        text: "Les bénévoles t’offrent un rouleau de ruban gaffer et une responsabilité non rémunérée. C’est le plus grand honneur du LAN.",
      };
    }
    if (this.save.stats.suspicion >= 70) {
      return {
        title: "La légende du sac qui glougloute",
        text: "Tu es branché, surveillé et étonnamment hydraté. L’agent Pare-Feu raconte encore ton passage à chaque relève.",
      };
    }
    return {
      title: "Participant officiellement branché",
      text: "Après le parking, le badge, le scanner, Kevin et un démon réseau, tu peux enfin télécharger la mise à jour qui t’empêche de jouer.",
    };
  }

  private resize(): void {
    const width = Math.max(1, this.ui.canvasHost.clientWidth || window.innerWidth);
    const height = Math.max(1, this.ui.canvasHost.clientHeight || window.innerHeight);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  private freshSave(): SaveData {
    const save = structuredClone(DEFAULT_SAVE);
    save.startedAt = Date.now();
    save.elapsedSeconds = 0;
    save.flags = {};
    save.lore = [];
    save.inventory = [...DEFAULT_SAVE.inventory];
    save.stats = { ...DEFAULT_SAVE.stats };
    return save;
  }

  private loadSave(): SaveData | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const save = JSON.parse(raw) as SaveData;
      if (
        save.version !== 1 ||
        !CHAPTERS[save.chapter] ||
        !save.stats ||
        !Array.isArray(save.inventory)
      ) {
        return null;
      }

      // Avant le portage, Gisèle était un objet d'inventaire de départ. Une
      // partie reprise au prologue sans rien dans les bras ni dans le coffre ne
      // la possède pas : elle est encore posée par terre.
      if (
        save.chapter === "prologue" &&
        !save.flags?.hands &&
        !save.flags?.trunk &&
        Array.isArray(save.inventory)
      ) {
        save.inventory = save.inventory.filter((item) => !CARGO[item]);
      }
      return save;
    } catch {
      return null;
    }
  }

  private persist(): void {
    if (!this.running) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.save));
  }

  private loadSettings(): GameSettings {
    try {
      const loaded = JSON.parse(
        localStorage.getItem(SETTINGS_KEY) ?? "{}",
      ) as Partial<GameSettings>;
      return {
        ...DEFAULT_SETTINGS,
        ...loaded,
        lookSensitivity: clampLookSensitivity(
          Number(loaded.lookSensitivity ?? DEFAULT_LOOK_SENSITIVITY),
        ),
      };
    } catch {
      return { ...DEFAULT_SETTINGS };
    }
  }

  private applySettingsClasses(): void {
    document.documentElement.classList.toggle(
      "high-contrast",
      this.settings.highContrast,
    );
    document.documentElement.classList.toggle(
      "reduced-motion",
      this.settings.reducedMotion,
    );
  }

  private flag(name: string): boolean {
    return Boolean(this.save.flags[name]);
  }

  private setFlag(name: string, value: boolean | number | string = true): void {
    this.save.flags[name] = value;
  }

  private hasItem(id: string): boolean {
    return this.save.inventory.includes(id);
  }

  private addItem(id: string): void {
    if (!this.hasItem(id)) this.save.inventory.push(id);
  }

  private removeItem(id: string): void {
    this.save.inventory = this.save.inventory.filter((item) => item !== id);
  }

  private addScore(value: number): void {
    this.save.stats.score = Math.max(0, this.save.stats.score + value);
  }

  private addSuspicion(value: number): void {
    this.save.stats.suspicion += value;
  }

  private addReputation(value: number): void {
    this.save.stats.reputation += value;
  }

  private normalizeStats(): void {
    this.save.stats.energy = THREE.MathUtils.clamp(this.save.stats.energy, 0, 100);
    this.save.stats.suspicion = THREE.MathUtils.clamp(
      this.save.stats.suspicion,
      0,
      100,
    );
    this.save.stats.reputation = THREE.MathUtils.clamp(
      this.save.stats.reputation,
      0,
      100,
    );
  }

  private finePointer(): boolean {
    return window.matchMedia("(pointer: fine)").matches;
  }
}
